import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { toast } from "sonner";
import { tokenStore } from "../lib/tokenStore";
import { WalkOnsPanel } from "../components/WalkOnsPanel";
import { CharacterDirectionPanel } from "../components/CharacterDirectionPanel";
import { StandingNotesPanel } from "../components/StandingNotesPanel";
import { ChevronLeft, Feather, Loader2, Pencil, RefreshCw, Check, X, Download, BookMarked, Rewind, BookOpen, Plus, Trash2, NotebookPen, Sparkles, Wand2, Heart, Leaf, HeartCrack, Sunrise, Moon, PenLine, MessageCircle, UserCircle2, Users, Volume2, Pause, Clock } from "lucide-react";

const ENDING_FLAVORS = [
  { key: "happy",       label: "Happy",        hint: "They arrive at what they yearned for",    Icon: Heart },
  { key: "bittersweet", label: "Bittersweet",  hint: "Tender, tinged with loss",                 Icon: Leaf },
  { key: "tragic",      label: "Tragic",       hint: "Heartbreak, separation, consequence",      Icon: HeartCrack },
  { key: "open",        label: "Open",         hint: "Ambiguous — reader imagines the rest",     Icon: Sunrise },
  { key: "cliffhanger", label: "Cliffhanger",  hint: "Unresolved but charged",                   Icon: Moon },
  { key: "ai",          label: "Author's choice", hint: "Whatever the arc most deserves",        Icon: Sparkles },
];

const REWRITE_PRESETS = [
  { key: "darker",   label: "Darker",          instruction: "Make this passage darker in mood — heighten unease, weight, or menace while keeping the action identical." },
  { key: "slower",   label: "Slower",          instruction: "Slow this passage down — linger on sensation and interior thought. Same events, more breath." },
  { key: "sensual",  label: "More sensual",    instruction: "Make this passage more sensual — charged, embodied, with closer attention to touch, breath, and heat." },
  { key: "tender",   label: "More tender",     instruction: "Soften this passage — add tenderness, warmth, and emotional openness. Same events, gentler register." },
  { key: "tighter",  label: "Tighter",         instruction: "Tighten this passage — cut filler, sharpen verbs, keep only what lands. Same meaning, leaner prose." },
];

// Parse chat-mode chapter text into an ordered list of beats.
// Supported line shapes:
//   "NARRATION: <text>"  →  { type: "narration", text }
//   "<Name>: <text>"     →  { type: "msg", sender, text }
// Anything else is treated as a continuation of the previous beat.
const parseChatContent = (raw) => {
  const beats = [];
  const lines = (raw || "").split(/\r?\n/);
  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;
    const narrMatch = t.match(/^NARRATION\s*[:\-–—]\s*(.*)$/i);
    if (narrMatch) {
      beats.push({ type: "narration", text: narrMatch[1].trim() });
      continue;
    }
    const msgMatch = t.match(/^([A-Za-z][A-Za-z .'-]{0,40}?)\s*:\s*(.*)$/);
    if (msgMatch && msgMatch[2]) {
      beats.push({ type: "msg", sender: msgMatch[1].trim(), text: msgMatch[2].trim() });
      continue;
    }
    // Continuation: append to previous msg/narration
    if (beats.length > 0) {
      const prev = beats[beats.length - 1];
      prev.text = (prev.text ? prev.text + " " : "") + t;
    } else {
      beats.push({ type: "narration", text: t });
    }
  }
  return beats;
};

// Parse screenplay-format content (scene mode):
// Parse screenplay-format content (scene mode):
//   "NAME: dialogue"  -> { type: "dialogue", speaker: "NAME", segments: [...], anchor }
//   "*action line*"   -> { type: "action", text: "...", anchor }
//
// CONTINUATION LINES: once a `NAME:` line appears, every non-blank line that
// follows is treated as more content from that same speaker (its dialogue and
// any `*action*` chunks are appended to the same beat's segments). A blank line
// resets the speaker; a new `NAME:` line starts a fresh beat.
//
// Inline `**bold**` is normalized to `*italic*` first so the AI's markdown
// drift doesn't break the layout.
const parseSceneContent = (raw) => {
  const beats = [];
  const normalized = (raw || "")
    .replace(/\*\*([^*]+)\*\*/g, "*$1*")
    // Collapse multi-line asterisk wraps: when a line starts with `*` (no closing) and a later
    // line is just `*`, fold the trailing `*` back onto the first line so the parser sees a
    // proper `*...*` pure-action line. Tolerates whitespace around the lone closing asterisk.
    .replace(/(\*[^*\n]+)\n\s*\*(\s*\n)/g, "$1*$2")
    // Same for opening-`*` lines followed by a line that ENDS with `*` (Claude sometimes
    // writes the dialogue body on the second line). Merge them into one line.
    .replace(/(\*[^*\n]+)\n([^*\n]+\*)/g, "$1 $2");
  const lines = normalized.split(/\r?\n/);
  let currentSpeakerBeat = null; // ref to the open dialogue beat being extended

  // Helper: split a body string into ordered dialogue / action segments.
  const splitSegments = (body) => {
    const out = [];
    const re = /\*([^*]+)\*/g;
    let cursor = 0;
    let mm;
    while ((mm = re.exec(body)) !== null) {
      const before = body.slice(cursor, mm.index).trim();
      if (before) out.push({ type: "dialogue", text: before });
      out.push({ type: "action", text: mm[1].trim() });
      cursor = mm.index + mm[0].length;
    }
    const tail = body.slice(cursor).trim();
    if (tail) out.push({ type: "dialogue", text: tail });
    return out;
  };

  // Helper: given a body string and the speaker who "owns" the start of it,
  // iteratively peel off mid-line `NAME:` speaker cues. Returns an array of beats —
  // typically 1 beat for clean dialogue, but multiple when Claude crams several
  // speakers onto one line like:
  //   CHRIS: *action* No. JAYNE: *action* Right. CHRIS: *action* Sure. JAYNE: *grins* Yes.
  // The pre-existing single-split version only handled the first switch, which
  // glued every subsequent switch to the wrong speaker bubble.
  const peelSpeakerBeats = (body, firstSpeaker, anchor, lineIdx) => {
    const out = [];
    let speaker = firstSpeaker;
    let remaining = body;
    // Safety cap (10 switches per line — way beyond any realistic chapter beat).
    for (let i = 0; i < 10; i++) {
      const mm = remaining.match(/^(.*?[.!?])\s+([A-Z][A-Z .'-]{0,30}?):\s*(.*)$/);
      if (!mm) break;
      const head = mm[1].trim();
      out.push({ type: "dialogue", speaker, segments: splitSegments(head), anchor, lineIdx });
      speaker = mm[2].trim().toUpperCase();
      remaining = mm[3].trim();
    }
    // Final tail (or the whole body if no cues found) → one beat for the last speaker.
    const tail = remaining.trim();
    out.push({ type: "dialogue", speaker, segments: tail ? splitSegments(tail) : [], anchor, lineIdx });
    return out;
  };

  let prevWasBlank = false;

  for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
    const line = lines[lineIdx];
    const t = line.trim();
    if (!t) {
      // Single blank lines DO NOT clear the speaker context — Claude habitually leaves a blank
      // between a `NAME: *action*` line and the character's actual dialogue/continuation below.
      // Only a DOUBLE blank (hard scene break) clears the speaker.
      if (prevWasBlank) {
        currentSpeakerBeat = null;
      }
      prevWasBlank = true;
      continue;
    }
    prevWasBlank = false;
    // Pure action line wrapped in single asterisks
    const isPureAction =
      t.startsWith("*") && t.endsWith("*") && t.length >= 3 && (t.match(/\*/g) || []).length === 2;

    // Drift recovery 1: Claude occasionally wraps an entire dialogue line in asterisks,
    // e.g. `*MARCUS: hey what are you doing*` — should render as dialogue, not a stage
    // direction. If the asterisk-interior parses as a NAME: text dialogue line, unwrap it.
    if (isPureAction) {
      const interior = t.slice(1, -1).trim();
      const dm = interior.match(/^([A-Za-z][A-Za-z .'-]{0,30}?)\s*:\s*(.+)$/);
      if (dm) {
        const speaker = dm[1].trim().toUpperCase();
        const body = dm[2].trim();
        const beat = { type: "dialogue", speaker, segments: splitSegments(body), anchor: t, lineIdx };
        beats.push(beat);
        currentSpeakerBeat = beat;
        continue;
      }
    }

    // Drift recovery 2: Claude sometimes uses a screenplay-style speaker cue on its own line,
    // e.g. `(Chris)` followed by asterisk-wrapped dialogue/action below. Treat the parenthetical
    // as opening a dialogue beat for that speaker — subsequent asterisk-wrapped lines and plain
    // lines (even across blank-line gaps) attach as segments until a new speaker cue.
    const pm = t.match(/^\(([A-Za-z][A-Za-z .'-]{0,30}?)\)\s*$/);
    if (pm) {
      const speaker = pm[1].trim().toUpperCase();
      const beat = { type: "dialogue", speaker, segments: [], anchor: t, lineIdx };
      beats.push(beat);
      currentSpeakerBeat = beat;
      continue;
    }

    // Drift recovery 3: `NAME:` ALONE on a line (no body after the colon). Claude sometimes
    // writes a bare speaker cue and puts the action / dialogue on the next line(s). Without
    // this case, the bare `NAME:` line falls through to the continuation branch and gets
    // glued onto the PREVIOUS speaker's bubble (and every following continuation/action line
    // sticks to that wrong speaker too). Treat it as opening an empty beat for the new speaker.
    const bareName = isPureAction ? null : t.match(/^([A-Za-z][A-Za-z .'-]{0,30}?)\s*:\s*$/);
    if (bareName) {
      const speaker = bareName[1].trim().toUpperCase();
      const beat = { type: "dialogue", speaker, segments: [], anchor: t, lineIdx };
      beats.push(beat);
      currentSpeakerBeat = beat;
      continue;
    }

    // Dialogue: NAME: text — short name + colon
    const m = isPureAction ? null : t.match(/^([A-Za-z][A-Za-z .'-]{0,30}?)\s*:\s*(.+)$/);

    if (m) {
      const speaker = m[1].trim().toUpperCase();
      const body = m[2].trim();
      // Drift recovery 4: body may contain ONE OR MORE embedded `NAME:` mid-text cues.
      // Iteratively peel them so every speaker gets their own beat (instead of every
      // mid-line switch being glued to the first speaker's bubble).
      const peeled = peelSpeakerBeats(body, speaker, t, lineIdx);
      for (const beat of peeled) {
        beats.push(beat);
        currentSpeakerBeat = beat;
      }
      continue;
    }

    if (isPureAction) {
      // Pure action attaches to the current speaker if any, else becomes orphan narration.
      if (currentSpeakerBeat) {
        currentSpeakerBeat.segments.push({ type: "action", text: t.slice(1, -1).trim() });
      } else {
        beats.push({ type: "action", text: t.slice(1, -1).trim(), anchor: t, lineIdx });
      }
      continue;
    }

    // Continuation / mixed line (no NAME prefix, not a pure action).
    if (currentSpeakerBeat) {
      // Drift recovery 5: continuation line may also contain ONE OR MORE embedded
      // `NAME:` cues. Iteratively peel them. The first segment attaches to the
      // current speaker; each subsequent cue opens a new beat for that speaker.
      const firstCue = t.match(/^(.*?[.!?])\s+([A-Z][A-Z .'-]{0,30}?):\s*(.*)$/);
      if (firstCue) {
        const head = firstCue[1].trim();
        const headSegs = splitSegments(head);
        if (headSegs.length === 0) {
          currentSpeakerBeat.segments.push({ type: "dialogue", text: head });
        } else {
          currentSpeakerBeat.segments.push(...headSegs);
        }
        const nextSpeaker = firstCue[2].trim().toUpperCase();
        const remainder = firstCue[3].trim();
        const peeled = peelSpeakerBeats(remainder, nextSpeaker, t, lineIdx);
        for (const beat of peeled) {
          beats.push(beat);
          currentSpeakerBeat = beat;
        }
        continue;
      }
      // Append parsed segments to the open speaker beat.
      const segs = splitSegments(t);
      if (segs.length === 0) {
        currentSpeakerBeat.segments.push({ type: "dialogue", text: t });
      } else {
        currentSpeakerBeat.segments.push(...segs);
      }
      continue;
    }

    // Orphan line with no speaker context. If it has asterisks, strip them and
    // render as an action; otherwise treat as orphan narration.
    if (t.includes("*")) {
      const cleaned = t.replace(/\*+/g, "").trim();
      if (cleaned) {
        beats.push({ type: "action", text: cleaned, anchor: t, lineIdx });
        continue;
      }
    }
    beats.push({ type: "action", text: t, anchor: t, lineIdx });
  }
  return beats;
};

// Drift health scan: cheap client-side regex scan of raw chapter content to surface drift
// patterns the user might want to clean up. Returns counts per category + an overall total.
// Cheap enough to run on every render — no LLM, no API call. Pairs with the existing
// `Fix scene drift` button which calls the backend normalizer.
const detectSceneDrift = (raw) => {
  const empty = { stackedSpeakers: 0, asteriskDialogue: 0, bareNameCues: 0, multilineWraps: 0, parenCues: 0, hollywoodCues: 0, total: 0 };
  if (!raw || typeof raw !== "string") return empty;
  const lines = raw.split(/\r?\n/);
  let stackedSpeakers = 0;
  let asteriskDialogue = 0;
  let bareNameCues = 0;
  let parenCues = 0;
  let hollywoodCues = 0;
  // Multi-line asterisk wrap: `*foo` on one line, closing `*` (alone or trailing) on the next.
  let multilineWraps = 0;
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (!t) continue;
    // `(Name)` parenthetical cue on its own line
    if (/^\([A-Za-z][A-Za-z .'-]{0,30}?\)\s*$/.test(t)) parenCues += 1;
    // Bare `NAME` on its own line (Hollywood layout)
    if (/^[A-Z][A-Z .'-]{1,30}$/.test(t) && !t.endsWith(":")) hollywoodCues += 1;
    // Bare `NAME:` (no body) — Claude opens an empty cue then puts content on next line
    if (/^[A-Za-z][A-Za-z .'-]{0,30}?\s*:\s*$/.test(t)) bareNameCues += 1;
    // `*NAME: text*` — full dialogue wrapped in asterisks
    if (/^\*[A-Za-z][A-Za-z .'-]{0,30}?\s*:\s*[^*]+\*$/.test(t)) asteriskDialogue += 1;
    // Stacked speakers on one line — at least one mid-line `NAME:` cue after a sentence terminator
    const m = t.match(/^([A-Za-z][A-Za-z .'-]{0,30}?)\s*:\s*(.+)$/);
    if (m) {
      const body = m[2];
      const matches = body.match(/(?:^|[.!?])\s+[A-Z][A-Z .'-]{0,30}?:/g);
      if (matches && matches.length > 0) stackedSpeakers += matches.length;
    }
    // Multi-line asterisk wrap: line opens with `*` but has no closing `*`, AND the next
    // non-blank line ends with `*` (possibly alone).
    if (t.startsWith("*") && (t.match(/\*/g) || []).length === 1) {
      for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
        const nt = lines[j].trim();
        if (!nt) continue;
        if (nt === "*" || (nt.endsWith("*") && (nt.match(/\*/g) || []).length === 1)) {
          multilineWraps += 1;
        }
        break;
      }
    }
  }
  const total = stackedSpeakers + asteriskDialogue + bareNameCues + multilineWraps + parenCues + hollywoodCues;
  return { stackedSpeakers, asteriskDialogue, bareNameCues, multilineWraps, parenCues, hollywoodCues, total };
};

// Render inline `*italic*` spans inside a dialogue or action line.
const renderInlineItalics = (text, keyPrefix) => {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(/(\*[^*]+\*)/g).filter(Boolean);
  return parts.map((part, i) => {
    if (part.startsWith("*") && part.endsWith("*") && part.length >= 3) {
      return (
        <em key={`${keyPrefix}-i-${i}`} className="italic text-stone-400 font-normal">
          {part.slice(1, -1)}
        </em>
      );
    }
    return <span key={`${keyPrefix}-t-${i}`}>{part}</span>;
  });
};

// Per-character tone palette. Tone numbers map to CSS classes (.scene-tone-{N}-name / -bub / -txt).
// 1-4 = blue / green / cyan / amber  → males & non-binary
// 5-8 = pink / violet / fuchsia / rose → females (& fallback for anyone else)
const _MALE_TONES = [1, 2, 3, 4];
const _FEMALE_TONES = [5, 6, 7, 8];
const _hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const buildSceneToneMap = (characters) => {
  const used = new Set();
  const map = {};
  const ordered = [...(characters || [])].sort((a, b) => (a.id || "").localeCompare(b.id || ""));
  for (const c of ordered) {
    const g = (c.gender || "").toLowerCase();
    const bucket = (g === "female" || g === "woman" || g === "f") ? _FEMALE_TONES
                 : (g === "male"   || g === "man"   || g === "m") ? _MALE_TONES
                 : [..._MALE_TONES, ..._FEMALE_TONES];
    const start = _hashStr(c.id || c.name || "") % bucket.length;
    let tone = bucket[start];
    let i = 0;
    while (used.has(tone) && i < bucket.length) {
      i++;
      tone = bucket[(start + i) % bucket.length];
    }
    used.add(tone);
    map[(c.name || "").trim().split(" ")[0].toUpperCase()] = tone;
  }
  return map;
};

const StoryReader = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [twistOn, setTwistOn] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBuffer, setEditBuffer] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [bibleInput, setBibleInput] = useState("");
  const [savingBible, setSavingBible] = useState(false);
  const [avoidPhraseInput, setAvoidPhraseInput] = useState("");
  const [savingAvoidPhrases, setSavingAvoidPhrases] = useState(false);
  const [detectedPhrases, setDetectedPhrases] = useState([]);
  const [loadingDetected, setLoadingDetected] = useState(false);
  // Cast view: { [chapterId]: { open: bool, loading: bool, cast: [{character_id, location, mood, relationships}] } }
  const [castState, setCastState] = useState({});
  const [savingReplyCount, setSavingReplyCount] = useState(false);
  const [savingBeatLength, setSavingBeatLength] = useState(false);
  // Per-turn chat reply-count override (resets after send)
  const [turnReplyCount, setTurnReplyCount] = useState(null);
  // Per-turn beat length override (resets after send)
  const [turnBeatLength, setTurnBeatLength] = useState(null);
  // Scene-mode tone intensity: 'muted' | 'standard' | 'vivid'. Persisted per-user via localStorage.
  const [sceneVibe, setSceneVibe] = useState(() => {
    try { return localStorage.getItem("inkwell.sceneVibe") || "standard"; } catch { return "standard"; }
  });
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("scene-vibe-muted", "scene-vibe-standard", "scene-vibe-vivid");
    root.classList.add(`scene-vibe-${sceneVibe}`);
    try { localStorage.setItem("inkwell.sceneVibe", sceneVibe); } catch { /* ignore */ }
  }, [sceneVibe]);
  const [avoidDraft, setAvoidDraft] = useState(null); // null = not editing, string = editing
  const [savingAvoid, setSavingAvoid] = useState(false);
  const [savingEmotional, setSavingEmotional] = useState(false);
  const [editingCanonId, setEditingCanonId] = useState(null);
  const [canonBuffer, setCanonBuffer] = useState("");
  const [streamingText, setStreamingText] = useState("");
  const [streamingIndex, setStreamingIndex] = useState(null);
  const [streamMode, setStreamMode] = useState(null); // "open" | "continue" | "regenerate" | "end"
  const [chapterProgress, setChapterProgress] = useState({});
  // Inline rewrite ("make this darker") state
  const [selectionAnchor, setSelectionAnchor] = useState(null); // { chapterId, text, x, y }
  const [rewriteBusy, setRewriteBusy] = useState(null); // instruction key while pending
  const [customInstruction, setCustomInstruction] = useState("");
  const [showCustomField, setShowCustomField] = useState(false);
  const [rewriteResult, setRewriteResult] = useState(null); // { chapterId, original, rewritten, newContent, instructionLabel }
  const [savingRewrite, setSavingRewrite] = useState(false);
  const popoverRef = useRef(null);
  // Ending picker
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [endingFlavor, setEndingFlavor] = useState("ai");
  const [endingCustom, setEndingCustom] = useState("");
  const [expandedSceneCanon, setExpandedSceneCanon] = useState(null); // chapter id whose canon is expanded
  const [regeneratingCanonId, setRegeneratingCanonId] = useState(null);
  const [composerMode, setComposerMode] = useState(null); // null = follow last chapter; "chat" | "prose" overrides
  const [directedQueue, setDirectedQueue] = useState([]); // array of AI character ids in order — for directed sends
  const [savingReplyMode, setSavingReplyMode] = useState(false);
  // Set Scene/Time beat — manually inject a `NARRATION: <text>` line into an open chat/scene chapter
  const [injectNarrationOpen, setInjectNarrationOpen] = useState(false);
  const [injectNarrationText, setInjectNarrationText] = useState("");
  const [injectingNarration, setInjectingNarration] = useState(false);

  // Active cast presence — toggle which AI characters are "in the scene" right now
  const [pendingToggleCharId, setPendingToggleCharId] = useState(null);  // id of char whose join/leave NARRATION is being composed
  const [pendingToggleAction, setPendingToggleAction] = useState(null);  // "join" | "leave"
  const [pendingNarrationText, setPendingNarrationText] = useState("");
  const [savingActiveCast, setSavingActiveCast] = useState(false);
  // Remove a character entirely from this story's cast. Strips them from `character_ids`,
  // every chapter's `active_character_ids` / `controlled_character_ids`, every Bible entry's
  // `scope_character_ids`, and clears `chat_player_id` if it was them. This is the fix for
  // the "AI keeps voicing the character even after I stepped them out of every scene" bug —
  // stepping out is per-chapter; removing from the story is permanent across the whole story.
  const removeCharacterFromStory = async (charId) => {
    const ch = (characters || []).find(c => c.id === charId);
    const name = ch?.name || "this character";
    const first = name.split(" ")[0];
    const isPlayer = story?.chat_player_id === charId;
    const warning = isPlayer
      ? `Remove ${name} from this story entirely?\n\nThis character is your PLAYER (the one you voice). Removing them will clear that setting — you'll need to pick a new player to keep using chat/scene mode.\n\nThis also strips ${first} from every scene's active cast and every Bible entry. This cannot be undone via the UI.\n\nContinue?`
      : `Remove ${name} from this story entirely?\n\nThe AI will never voice ${first} again in this story. This also strips ${first} from every scene's active cast and every Bible entry's scope.\n\nThis cannot be undone via the UI. Continue?`;
    if (!confirm(warning)) return;
    try {
      await api.delete(`/stories/${id}/characters/${charId}`);
      await load();
      toast.success(`${first} removed from this story.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove character");
    }
  };

  // Drag-to-reorder state for active cast pills (sets auto-mode reply priority)
  const [dragCharId, setDragCharId] = useState(null);
  const [dragOverCharId, setDragOverCharId] = useState(null);
  // Manage-cast expander: shows the per-character "Remove from story" controls.
  const [manageCastOpen, setManageCastOpen] = useState(false);
  // The full character library (all chars owned by the user) — lazy-loaded the first time
  // the Manage Cast expander opens. Used to populate the "Add character" dropdown so the
  // user can bring an existing library character into this story mid-narrative.
  const [libraryChars, setLibraryChars] = useState(null);
  const [pickCharToAdd, setPickCharToAdd] = useState("");
  const [savingAddChar, setSavingAddChar] = useState(false);

  // Add an existing library character to this story's cast mid-narrative.
  const addCharacterToStory = async () => {
    if (!pickCharToAdd) {
      toast.error("Pick a character to add.");
      return;
    }
    setSavingAddChar(true);
    try {
      await api.post(`/stories/${id}/characters`, { character_id: pickCharToAdd });
      await load();
      setPickCharToAdd("");
      toast.success("Character added to this story.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add character");
    } finally {
      setSavingAddChar(false);
    }
  };

  // Lazy-load the user's library characters the first time Manage Cast expands.
  useEffect(() => {
    if (!manageCastOpen || libraryChars !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/characters");
        if (!cancelled) setLibraryChars(Array.isArray(r.data) ? r.data : []);
      } catch (_err) {
        if (!cancelled) setLibraryChars([]);
      }
    })();
    return () => { cancelled = true; };
  }, [manageCastOpen, libraryChars]);

  // Render the Manage Cast panel — used in BOTH chat AND scene composers, AND in prose mode
  // when the story has a cast (so the user can always add/remove characters regardless of
  // active-cast panel visibility, which requires aiChars >= 2 to render).
  const renderManageCastPanel = () => {
    if (!lastChapter?.is_open) return null;
    if (!(characters && characters.length > 0)) return null;
    const inStory = new Set((story?.character_ids) || []);
    const available = (libraryChars || []).filter(c => !inStory.has(c.id));
    const capReached = (characters || []).length >= 4 && (story?.mode === "chat" || story?.mode === "scene" || !!story?.chat_player_id);
    return (
      <div data-testid="manage-cast-panel" className="border border-stone-800/60 bg-stone-900/30 rounded-2xl p-3 space-y-2">
        <button
          type="button"
          data-testid="manage-cast-toggle"
          onClick={() => setManageCastOpen(o => !o)}
          className="w-full flex items-center justify-between gap-2 px-1 py-1 text-stone-500 hover:text-rose-300 font-sans-ui text-[9px] uppercase tracking-[0.25em] transition"
        >
          <span className="flex items-center gap-2">
            <Users className="w-3 h-3" strokeWidth={1.5} />
            {manageCastOpen ? "▾ Manage cast" : "▸ Manage cast"}
          </span>
          <span className="text-stone-700 normal-case tracking-normal text-[10px]">add / remove</span>
        </button>
        {manageCastOpen && (
          <div className="space-y-1 pt-1">
            {(characters || []).map(c => {
              const isPlayer = story?.chat_player_id === c.id;
              const first = (c.name || "").split(" ")[0];
              return (
                <div key={c.id} className="flex items-center justify-between gap-2 px-1">
                  <span className="font-serif-reading italic text-stone-400 text-xs">
                    {c.name}
                    {isPlayer && <span className="ml-2 font-sans-ui not-italic text-[9px] uppercase tracking-[0.2em] text-rose-700">player</span>}
                  </span>
                  <button
                    type="button"
                    data-testid={`cast-remove-${c.id}`}
                    onClick={() => removeCharacterFromStory(c.id)}
                    title={`Remove ${first} from this story entirely`}
                    className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600 hover:text-rose-300 px-2 py-1 border border-stone-800 hover:border-rose-800 transition"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
            {libraryChars === null && (
              <div className="px-1 pt-2 border-t border-stone-800/60 font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-700">
                Loading library…
              </div>
            )}
            {libraryChars !== null && capReached && (
              <div className="px-1 pt-2 border-t border-stone-800/60 font-serif-reading italic text-[11px] text-stone-600">
                Cast is full (4 max for chat/scene). Remove someone first to add another.
              </div>
            )}
            {libraryChars !== null && !capReached && available.length === 0 && (
              <div className="px-1 pt-2 border-t border-stone-800/60 font-serif-reading italic text-[11px] text-stone-600">
                No other characters in your library. Create one from the Cast page to add them here.
              </div>
            )}
            {libraryChars !== null && !capReached && available.length > 0 && (
              <div data-testid="add-character-picker" className="px-1 pt-2 border-t border-stone-800/60 space-y-1.5">
                <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600">
                  Add character to story
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid="add-character-select"
                    value={pickCharToAdd}
                    onChange={(e) => setPickCharToAdd(e.target.value)}
                    disabled={savingAddChar}
                    className="flex-1 bg-stone-900 border border-stone-800 text-stone-100 font-serif-reading text-xs px-2 py-1.5 rounded-sm focus:outline-none focus:border-rose-800"
                  >
                    <option value="">— pick a character —</option>
                    {available.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    data-testid="add-character-btn"
                    onClick={addCharacterToStory}
                    disabled={!pickCharToAdd || savingAddChar}
                    className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-rose-300 px-2 py-1.5 border border-rose-800 hover:bg-rose-950/40 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    {savingAddChar ? "Adding…" : "Add"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  const [normalizingDrift, setNormalizingDrift] = useState(false);
  // Player-picker dialog: shown when user clicks "Texts" on a prose-started story
  // that doesn't yet have a chat_player_id set.
  const [playerPickerOpen, setPlayerPickerOpen] = useState(false);
  const [playerPickerChoice, setPlayerPickerChoice] = useState("");
  // What mode the player picker is enabling — "chat" (Texts) or "scene". After save, the
  // composer switches to this mode automatically.
  const [playerPickerTarget, setPlayerPickerTarget] = useState("chat");
  const [savingPlayer, setSavingPlayer] = useState(false);
  const chapterRefs = useRef({});
  const prevChapterCountRef = useRef(0);
  const streamingRef = useRef(null);
  // Synchronous double-fire guard that reads/writes within the same JS task — React state
  // (`generating`) only flips on re-render which can trail by a tick, so a rapid-fire caller
  // could slip past the state check. This ref is the true barrier.
  const generatingRef = useRef(false);
  // Per-chapter rendered-body cache: long scenes (1000s of beats) must not re-render on every keystroke/token.
  const bodyCacheRef = useRef(new Map());
  const openAttemptedRef = useRef(false);

  // Voice playback (TTS via portrait server proxy)
  // Key per bubble = `${chapterId}:${beatIndex}`. Tracks loading + playing state.
  const [voiceState, setVoiceState] = useState({ key: null, status: "idle" }); // status: "idle" | "loading" | "playing"
  const audioRef = useRef(null);

  // Face-to-Face Mode (Feature B) — toggled on/off, shows portrait video panel.
  // When ON, the ▶ button on bubbles will use /portrait/lipsync (returns mp4 with embedded audio)
  // instead of /portrait/tts (audio only). The video plays in the f2fVideoRef element.
  const [faceToFaceOn, setFaceToFaceOn] = useState(false);
  const [idleVideoUrl, setIdleVideoUrl] = useState(null); // blob URL for the cached idle loop
  const [idleLoading, setIdleLoading] = useState(false);
  const [activeLipsyncUrl, setActiveLipsyncUrl] = useState(null); // blob URL of the currently-playing lipsync mp4
  const f2fVideoRef = useRef(null);
  const idleBlobRef = useRef(null); // remember so we can revoke later
  const lipsyncBlobRef = useRef(null);
  // Increments on every playVoice call; in-flight async work compares against
  // its captured snapshot to detect "I've been cancelled / superseded" without
  // needing AbortController plumbing through axios.
  const voiceSessionRef = useRef(0);

  // Helper: extract a useful error detail when responseType=blob.
  const _extractBlobError = async (err, fallback) => {
    let detail = fallback;
    if (err?.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const j = JSON.parse(text);
        detail = j.detail || detail;
      } catch (_e) { /* keep default */ }
    } else if (err?.response?.data?.detail) {
      detail = err.response.data.detail;
    } else if (err?.message) {
      detail = err.message;
    }
    return detail;
  };

  const playVoice = useCallback(async (key, text, voiceId, opts = {}) => {
    const { portraitUrl = "", portraitId = "" } = opts;
    // Toggle: if same bubble is playing/loading, stop it.
    if (voiceState.key === key && (voiceState.status === "playing" || voiceState.status === "loading")) {
      voiceSessionRef.current += 1; // invalidate any in-flight callbacks
      try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
      audioRef.current = null;
      try { f2fVideoRef.current?.pause(); } catch (_e) { /* ignore */ }
      if (lipsyncBlobRef.current) { URL.revokeObjectURL(lipsyncBlobRef.current); lipsyncBlobRef.current = null; }
      setActiveLipsyncUrl(null);
      setVoiceState({ key: null, status: "idle" });
      return;
    }
    // Stop any other playing audio/video first
    voiceSessionRef.current += 1;
    try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
    audioRef.current = null;
    try { f2fVideoRef.current?.pause(); } catch (_e) { /* ignore */ }
    if (lipsyncBlobRef.current) { URL.revokeObjectURL(lipsyncBlobRef.current); lipsyncBlobRef.current = null; }
    setActiveLipsyncUrl(null);

    if (!voiceId) {
      toast.error("This character has no voice ID set. Add one in Characters.");
      return;
    }

    // Capture session snapshot — every async resolver below short-circuits if
    // the user has clicked another bubble or toggled stop.
    const mySession = ++voiceSessionRef.current;
    setVoiceState({ key, status: "loading" });

    // Branch: Face-to-Face Mode → pipelined audio + video.
    // Both API calls fire IN PARALLEL. Audio (TTS, ~1-2s) starts playback
    // immediately; the much-slower lipsync video (~15-90s) silently catches
    // up later by muting the audio element and seeking the video to match.
    const useLipsync = faceToFaceOn && !!portraitUrl;
    if (useLipsync) {
      const ttsPromise = api.post(
        "/portrait/tts",
        { text, voice_id: voiceId },
        { responseType: "blob" }
      );
      const lipsyncPromise = api.post(
        "/portrait/lipsync",
        { text, voice_id: voiceId, portrait_url: portraitUrl, portrait_id: portraitId || undefined },
        { responseType: "blob" }
      );

      // Phase 1 — audio arrives first (1-2s typical)
      ttsPromise.then((r) => {
        if (voiceSessionRef.current !== mySession) return; // superseded
        const blobUrl = URL.createObjectURL(r.data);
        // Reuse a single Audio element across all plays — avoids dynamic
        // creation that confuses third-party session recorders (PostHog) in
        // dev preview, and is leaner anyway.
        if (!audioRef.current) audioRef.current = new Audio();
        const audio = audioRef.current;
        audio.muted = false; // reset from any prior video-sync state
        audio.src = blobUrl;
        // onended/onerror = property assignments (auto-replace prior handler)
        audio.onended = () => {
          if (voiceSessionRef.current !== mySession) return;
          if (!lipsyncBlobRef.current) {
            URL.revokeObjectURL(blobUrl);
            setVoiceState({ key: null, status: "idle" });
          }
        };
        audio.onerror = () => {
          if (voiceSessionRef.current !== mySession) return;
          URL.revokeObjectURL(blobUrl);
          setVoiceState({ key: null, status: "idle" });
          toast.error("Audio playback failed");
        };
        audio.play().catch(() => { /* user gesture already happened */ });
        setVoiceState({ key, status: "playing" });
      }).catch(async (err) => {
        if (voiceSessionRef.current !== mySession) return;
        setVoiceState({ key: null, status: "idle" });
        toast.error(await _extractBlobError(err, "Voice generation failed"));
      });

      // Phase 2 — lipsync catches up, swap idle→lipsync video synced to audio
      lipsyncPromise.then((r) => {
        if (voiceSessionRef.current !== mySession) return; // superseded
        if (lipsyncBlobRef.current) URL.revokeObjectURL(lipsyncBlobRef.current);
        const blobUrl = URL.createObjectURL(r.data);
        lipsyncBlobRef.current = blobUrl;
        setActiveLipsyncUrl(blobUrl);
        // One tick for React to mount the <video> element with this src
        setTimeout(() => {
          if (voiceSessionRef.current !== mySession) return;
          const video = f2fVideoRef.current;
          const audio = audioRef.current;
          if (!video) return;
          // If audio is still playing → mute it and seek video to match
          if (audio && !audio.ended && !audio.paused) {
            const t = audio.currentTime || 0;
            audio.muted = true;
            try { video.currentTime = Math.min(t, (video.duration || t) - 0.05); } catch (_e) { /* not loaded yet */ }
          }
          // (If audio already ended, video plays from 0 as a brief replay —
          //  acceptable; user just heard them say it, now they see them say it.)
          video.onended = () => {
            if (voiceSessionRef.current !== mySession) return;
            if (lipsyncBlobRef.current === blobUrl) {
              URL.revokeObjectURL(blobUrl);
              lipsyncBlobRef.current = null;
              setActiveLipsyncUrl(null);
            }
            // Stop any leftover (now-muted) audio
            try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
            audioRef.current = null;
            setVoiceState({ key: null, status: "idle" });
          };
          video.play().catch(() => { /* autoplay should be fine — user-initiated chain */ });
        }, 50);
      }).catch(async (err) => {
        if (voiceSessionRef.current !== mySession) return;
        // Lipsync failed silently — don't disrupt the audio that's probably already playing.
        const detail = await _extractBlobError(err, "(lipsync background fetch failed — audio still playing)");
        // eslint-disable-next-line no-console
        console.warn("[F2F lipsync background failed]", detail);
      });

      return;
    }

    // Default branch: audio-only playback (no F2F)
    try {
      const r = await api.post(
        "/portrait/tts",
        { text, voice_id: voiceId },
        { responseType: "blob" }
      );
      if (voiceSessionRef.current !== mySession) return;
      const blobUrl = URL.createObjectURL(r.data);
      if (!audioRef.current) audioRef.current = new Audio();
      const audio = audioRef.current;
      audio.muted = false;
      audio.src = blobUrl;
      audio.onended = () => {
        if (voiceSessionRef.current !== mySession) return;
        URL.revokeObjectURL(blobUrl);
        setVoiceState({ key: null, status: "idle" });
      };
      audio.onerror = () => {
        if (voiceSessionRef.current !== mySession) return;
        URL.revokeObjectURL(blobUrl);
        setVoiceState({ key: null, status: "idle" });
        toast.error("Audio playback failed");
      };
      await audio.play();
      setVoiceState({ key, status: "playing" });
    } catch (err) {
      if (voiceSessionRef.current !== mySession) return;
      setVoiceState({ key: null, status: "idle" });
      toast.error(await _extractBlobError(err, "Voice generation failed"));
    }
  }, [voiceState, faceToFaceOn]);

  // Stop audio when leaving the page
  useEffect(() => () => {
    voiceSessionRef.current += 1; // invalidate any in-flight callbacks
    try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
    if (audioRef.current) { audioRef.current.src = ""; audioRef.current = null; }
    if (idleBlobRef.current) { URL.revokeObjectURL(idleBlobRef.current); idleBlobRef.current = null; }
    if (lipsyncBlobRef.current) { URL.revokeObjectURL(lipsyncBlobRef.current); lipsyncBlobRef.current = null; }
  }, []);

  // F2F idle pre-fetch is wired to (faceToFaceOn + the active speaker's portrait URL).
  // We compute those deps inside the effect so this hook stays in unconditional position
  // (must not run after early-return-on-loading branches).
  const _activePortraitUrl = (() => {
    const _aiChars = (data?.characters || []).filter(c => c.id !== data?.story?.chat_player_id);
    return _aiChars.find(c => !!c.portrait_url)?.portrait_url || "";
  })();
  const _activeSpeakerId = (() => {
    const _aiChars = (data?.characters || []).filter(c => c.id !== data?.story?.chat_player_id);
    return _aiChars.find(c => !!c.portrait_url)?.id || "";
  })();

  useEffect(() => {
    if (!faceToFaceOn || !_activePortraitUrl) return;
    let cancelled = false;
    setIdleLoading(true);
    (async () => {
      try {
        const r = await api.post(
          "/portrait/idle",
          { portrait_url: _activePortraitUrl, portrait_id: _activeSpeakerId || undefined },
          { responseType: "blob" }
        );
        if (cancelled) return;
        if (idleBlobRef.current) URL.revokeObjectURL(idleBlobRef.current);
        const url = URL.createObjectURL(r.data);
        idleBlobRef.current = url;
        setIdleVideoUrl(url);
      } catch (err) {
        if (cancelled) return;
        const detail = await _extractBlobError(err, "Idle render failed (first run can take 30–90s)");
        toast.error(detail);
      } finally {
        if (!cancelled) setIdleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [faceToFaceOn, _activePortraitUrl, _activeSpeakerId]);

  useEffect(() => {
    if (faceToFaceOn) return;
    if (idleBlobRef.current) { URL.revokeObjectURL(idleBlobRef.current); idleBlobRef.current = null; }
    if (lipsyncBlobRef.current) { URL.revokeObjectURL(lipsyncBlobRef.current); lipsyncBlobRef.current = null; }
    setIdleVideoUrl(null);
    setActiveLipsyncUrl(null);
  }, [faceToFaceOn]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/stories/${id}`);
      setData(r.data);
      return r.data;
    } catch {
      toast.error("Story not found");
      return null;
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Light refresh after a turn: fetch only the last few chapters and merge them in, instead of
  // re-downloading every chapter of a long story.
  const tailReqRef = useRef(0);
  const loadTail = useCallback(async () => {
    const reqId = ++tailReqRef.current;
    try {
      const r = await api.get(`/stories/${id}?tail=4`);
      if (reqId !== tailReqRef.current) return; // a newer turn's refresh superseded this one
      const tail = r.data.chapters || [];
      setData(prev => {
        if (!prev || !tail.length) return prev ? { ...prev, ...r.data, chapters: prev.chapters } : r.data;
        const minIdx = Math.min(...tail.map(c => c.index));
        const head = (prev.chapters || []).filter(c => c.index < minIdx);
        return { ...prev, ...r.data, chapters: [...head, ...tail] };
      });
    } catch {
      // Non-critical: the done event already patched the new chapter in.
    }
  }, [id]);

  // Consume an SSE stream. action: "open" | "continue" | "regenerate" | "end"
  const streamChapter = useCallback(async (action, payload) => {
    const url = {
      open: `/api/stories/${id}/open-stream`,
      continue: `/api/stories/${id}/continue-stream`,
      regenerate: `/api/stories/${id}/regenerate-stream`,
      end: `/api/stories/${id}/end-stream`,
    }[action];
    const token = tokenStore.getToken();
    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify(payload || {}),
    });
    if (!resp.ok || !resp.body) {
      const err = await resp.json().catch(() => ({ detail: "Stream failed" }));
      throw new Error(err.detail || "Stream failed");
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let gotDone = false;
    let doneChapter = null;
    // Batch token renders (~12/s) — re-rendering the whole reader per token lags on long scenes.
    let pendingText = "";
    let flushTimer = null;
    const flushTokens = () => {
      flushTimer = null;
      if (!pendingText) return;
      const chunk = pendingText;
      pendingText = "";
      setStreamingText(prev => prev + chunk);
    };
    setStreamingText("");
    setStreamMode(action);
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const block of events) {
        const lines = block.split("\n");
        const evt = lines.find(l => l.startsWith("event: "))?.slice(7);
        const dataLine = lines.find(l => l.startsWith("data: "))?.slice(6);
        if (!dataLine) continue;
        let parsed;
        try { parsed = JSON.parse(dataLine); } catch { continue; }
        if (evt === "start") {
          setStreamingIndex(parsed.index);
          if (parsed.mode) setStreamMode(parsed.mode);
        }
        else if (evt === "token") {
          pendingText += parsed.t || "";
          if (!flushTimer) flushTimer = setTimeout(flushTokens, 80);
        }
        else if (evt === "done") { gotDone = true; doneChapter = parsed; }
        else if (evt === "error") {
          if (flushTimer) clearTimeout(flushTimer);
          throw new Error(parsed.detail || "Stream error");
        }
      }
    }
    if (flushTimer) clearTimeout(flushTimer);
    flushTokens();
    if (!gotDone) throw new Error("Stream ended unexpectedly");
    return doneChapter;
  }, [id]);

  // Patch the finished chapter into local state so the composer unlocks immediately,
  // then refresh the full story in the background (long stories = large payloads).
  const applyDoneChapter = useCallback((ch) => {
    tailReqRef.current += 1; // invalidate any in-flight tail refresh from an earlier turn
    if (!ch?.id) return load();
    setData(prev => {
      if (!prev) return prev;
      const chapters = prev.chapters || [];
      const exists = chapters.some(c => c.id === ch.id);
      return { ...prev, chapters: exists ? chapters.map(c => (c.id === ch.id ? { ...c, ...ch } : c)) : [...chapters, ch] };
    });
    loadTail();
  }, [load, loadTail]);

  // Send a chat continuation. Routes to nudge if the textbox is empty + queue has targets.
  const sendChatTurn = () => {
    if (generating) return;  // double-fire guard (mobile taps)
    const hasText = freeText.trim().length > 0;
    if (!hasText && directedQueue.length > 0) {
      // Pure nudge: AI speaks based on existing scene + queue order, no player message.
      doNudgeWithQueue();
    } else {
      continueStory({ free_text: freeText });
    }
  };

  const doNudgeWithQueue = async () => {
    if (generating || directedQueue.length === 0) return;
    setGenerating(true);
    try {
      applyDoneChapter(await streamChapter("continue", { free_text: "", target_ai_ids: directedQueue }));
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Nudge failed");
    } finally {
      setGenerating(false);
      setStreamingText("");
      setStreamingIndex(null);
      setStreamMode(null);
      setDirectedQueue([]);
    }
  };

  // Nudge a specific list of AI character ids — used for single-AI chats where
  // there's no directed-queue UI but the user wants the AI to keep talking.
  const doNudgeForCharIds = async (charIds) => {
    if (generating || !charIds || charIds.length === 0) return;
    setGenerating(true);
    try {
      applyDoneChapter(await streamChapter("continue", { free_text: "", target_ai_ids: charIds }));
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Nudge failed");
    } finally {
      setGenerating(false);
      setStreamingText("");
      setStreamingIndex(null);
      setStreamMode(null);
    }
  };

  const continueStory = async (payload) => {
    // Guard: prevent double-fire from mobile double-taps or rapid button presses.
    // Uses a ref for synchronous barrier (state updates don't flush between same-task calls).
    if (generatingRef.current || generating) {
      console.warn("continueStory: already generating — ignoring duplicate call");
      return;
    }
    if (chapterMode(lastChapter) === "scene" && /[([]\s*(?:(?:AI|A\.I\.|OOC)\s*[,:;–—-]\s+|note\s+(?:to\s+)?(?:the\s+)?AI\s*[,:;–—-]?\s+)[^()[\]]{3,}?\s*[)\]]/i.test(payload?.free_text || "")) {
      toast.message("Saved as a standing note for the AI", { description: "It's hidden from the scene. Manage it under Standing notes." });
    }
    generatingRef.current = true;
    setGenerating(true);
    try {
      // Pass the composer's effective mode if any mixed-mode toggle is available,
      // so the backend can switch formats per chapter (prose / scene / chat).
      const augmented = canShowFormatToggle ? { ...payload, mode: composerEffectiveMode } : payload;
      // Per-turn beat length override (scene mode)
      if (composerEffectiveMode === "scene" && turnBeatLength) augmented.beat_length = turnBeatLength;
      // Per-turn chat reply count override (chat mode, auto reply mode)
      if (composerEffectiveMode === "chat" && replyMode !== "directed" && turnReplyCount) augmented.chat_reply_count = turnReplyCount;
      // In chat + directed mode, attach the queued AI ids
      if (composerEffectiveMode === "chat" && replyMode === "directed" && directedQueue.length > 0) {
        augmented.target_ai_ids = directedQueue;
      }
      // In scene + directed mode, attach the queued AI ids
      if (composerEffectiveMode === "scene" && replyMode === "directed" && directedQueue.length > 0) {
        augmented.target_ai_ids = directedQueue;
      }
      applyDoneChapter(await streamChapter("continue", augmented));
      toast.success("The story turns.");
    } catch (err) {
      toast.error(err.message || "Generation failed");
    } finally {
      generatingRef.current = false;
      setGenerating(false);
      setStreamingText("");
      setStreamingIndex(null);
      setStreamMode(null);
      setFreeText("");
      setComposerMode(null); // reset override after each send so it follows the new last chapter
      setDirectedQueue([]); // clear chip queue after each send
      setTurnBeatLength(null);
      setTurnReplyCount(null);
    }
  };

  // (Single-tap instant-nudge replaced with queued nudge — see sendChatTurn / doNudgeWithQueue.)

  const setStoryReplyMode = async (newMode) => {
    setSavingReplyMode(true);
    try {
      await api.patch(`/stories/${id}/chat-reply-mode`, { chat_reply_mode: newMode });
      await load();
      toast.success(newMode === "auto" ? "Replies: natural" : "Replies: directed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change reply mode");
    } finally {
      setSavingReplyMode(false);
    }
  };

  // Enable texting OR scene mode mid-story on a prose-started story by setting chat_player_id.
  // The target mode is taken from `playerPickerTarget` so the composer switches to the right
  // format after the picker save.
  const enableTextingForStory = async () => {
    if (!playerPickerChoice) {
      toast.error("Pick which character you play as.");
      return;
    }
    setSavingPlayer(true);
    try {
      await api.patch(`/stories/${id}/chat-player`, { chat_player_id: playerPickerChoice });
      await load();
      const target = playerPickerTarget || "chat";
      setComposerMode(target);
      setPlayerPickerOpen(false);
      toast.success(
        target === "scene"
          ? "Scene mode enabled — switch formats anytime."
          : "Texting enabled — switch back to prose anytime."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not enable this mode");
    } finally {
      setSavingPlayer(false);
    }
  };

  // Rewind a chat scene to before a specific beat (player or AI bubble).
  // Drops that beat and everything after it. Scene stays open.
  // The robust path is `targetLineIdx` — the raw line index this beat parsed from.
  // It survives stacked-speaker splitting and frontend/backend parser drift because
  // it pins truncation to the exact source line rather than counting beats.
  const rewindChatToBeat = async (chapterId, beatIndex, anchor = null, targetLineIdx = null) => {
    if (beatIndex < 1 && !anchor && targetLineIdx == null) {
      toast.error("Can't rewind past the first message. Close the scene instead.");
      return;
    }
    if (!confirm("Delete this message and everything after it? You'll be able to send a new message in its place.")) return;
    try {
      const payload = { beat_index: beatIndex };
      if (anchor) payload.beat_anchor = anchor;
      if (targetLineIdx != null) payload.target_line_index = targetLineIdx;
      await api.post(`/stories/${id}/chapters/${chapterId}/rewind-chat`, payload);
      await load();
      toast.success("Rewound.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not rewind");
    }
  };

  const endScene = async () => {
    const isScene = lastChapterMode === "scene";
    const promptText = isScene
      ? "Close this scene? The AI will generate a canon summary of what just happened, and your next message will start a CLEAN new scene — fresh location, fresh time, no carried-over state. This is how to give the AI a hard break between, say, a bedroom scene and a kitchen scene."
      : "Close this scene? A summary will be saved and your next message will start a new scene.";
    if (!confirm(promptText)) return;
    try {
      await api.post(`/stories/${id}/break`);
      await load();
      toast.success("Scene closed. The next message starts a new one.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not close the scene");
    }
  };

  const submitInjectNarration = async () => {
    const text = injectNarrationText.trim();
    if (!text) return;
    const chapterId = lastChapter?.id;
    if (!chapterId) {
      toast.error("No open scene to insert a beat into.");
      return;
    }
    setInjectingNarration(true);
    try {
      await api.post(`/stories/${id}/chapters/${chapterId}/inject-narration`, { text });
      await load();
      setInjectNarrationText("");
      setInjectNarrationOpen(false);
      toast.success("Beat inserted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not insert beat");
    } finally {
      setInjectingNarration(false);
    }
  };

  // Open the cast-toggle confirmation panel for a character. Pre-fills a suggested NARRATION.
  const openCastToggle = (charId, action) => {
    const c = (characters || []).find(x => x.id === charId);
    if (!c) return;
    const first = (c.name || "").split(" ")[0] || c.name;
    const suggestion = action === "join"
      ? `${first} joins the conversation.`
      : `${first} steps out for a moment.`;
    setPendingToggleCharId(charId);
    setPendingToggleAction(action);
    setPendingNarrationText(suggestion);
  };

  const cancelCastToggle = () => {
    setPendingToggleCharId(null);
    setPendingToggleAction(null);
    setPendingNarrationText("");
  };

  const normalizeChapterDrift = async () => {
    const chapterId = lastChapter?.id;
    if (!chapterId) return;
    setNormalizingDrift(true);
    try {
      const r = await api.post(`/stories/${id}/chapters/${chapterId}/normalize-drift`);
      if (r.data?.changed) {
        await load();
        toast.success("Scene drift fixed.");
      } else {
        toast.success("No drift detected — chapter is already clean.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not normalize");
    } finally { setNormalizingDrift(false); }
  };

  const submitCastToggle = async () => {
    if (!pendingToggleCharId || !pendingToggleAction) return;
    const chapterId = lastChapter?.id;
    if (!chapterId) {
      toast.error("No open scene.");
      return;
    }
    // Compute new active list off the current chapter state
    const currentActive = (lastChapter?.active_character_ids && lastChapter.active_character_ids.length > 0)
      ? [...lastChapter.active_character_ids]
      : ((characters || []).map(c => c.id)); // legacy default
    let nextActive;
    if (pendingToggleAction === "join") {
      nextActive = currentActive.includes(pendingToggleCharId)
        ? currentActive
        : [...currentActive, pendingToggleCharId];
    } else {
      nextActive = currentActive.filter(cid => cid !== pendingToggleCharId);
    }
    setSavingActiveCast(true);
    try {
      const payload = { active_character_ids: nextActive };
      const narration = pendingNarrationText.trim();
      if (narration) payload.narration_text = narration;
      await api.patch(`/stories/${id}/chapters/${chapterId}/active-cast`, payload);
      await load();
      const c = (characters || []).find(x => x.id === pendingToggleCharId);
      const first = (c?.name || "").split(" ")[0];
      toast.success(pendingToggleAction === "join" ? `${first} is in the scene` : `${first} stepped out`);
      cancelCastToggle();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update cast");
    } finally {
      setSavingActiveCast(false);
    }
  };

  // Drag-reorder the active pills to set auto-mode speaking priority. Silent — no NARRATION beat.
  const reorderActiveCast = async (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const chapterId = lastChapter?.id;
    if (!chapterId) return;
    const currentActive = (lastChapter?.active_character_ids && lastChapter.active_character_ids.length > 0)
      ? [...lastChapter.active_character_ids]
      : ((characters || []).map(c => c.id));
    const srcIdx = currentActive.indexOf(sourceId);
    const tgtIdx = currentActive.indexOf(targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    const next = [...currentActive];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    setSavingActiveCast(true);
    try {
      await api.patch(`/stories/${id}/chapters/${chapterId}/active-cast`, { active_character_ids: next });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reorder cast");
    } finally {
      setSavingActiveCast(false);
    }
  };

  const endStory = async (payload = {}) => {
    if (generating) {
      console.warn("endStory: already generating — ignoring duplicate call");
      return;
    }
    setGenerating(true);
    try {
      await streamChapter("end", payload);
      await load();
      toast.success("The story closes.");
    } catch (err) {
      toast.error(err.message || "Could not end the story");
    } finally {
      setGenerating(false);
      setStreamingText("");
      setStreamingIndex(null);
      setStreamMode(null);
    }
  };

  const downloadStory = async (format) => {
    try {
      const token = tokenStore.getToken();
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stories/${id}/download?format=${format}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const disposition = resp.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `story.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Saved as ${filename}`);
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const openIfEmpty = useCallback(async (storyData) => {
    if (openAttemptedRef.current) return;
    if (!storyData || storyData.chapters?.length > 0) return;
    openAttemptedRef.current = true;
    setGenerating(true);
    try {
      await streamChapter("open");
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to open story");
    } finally {
      setGenerating(false);
      setStreamingText("");
      setStreamingIndex(null);
      setStreamMode(null);
    }
  }, [streamChapter, load]);

  useEffect(() => {
    if (data && data.chapters?.length === 0) openIfEmpty(data);
  }, [data, openIfEmpty]);

  const regenerateLast = async () => {
    if (regenerating || generating) {
      console.warn("regenerateLast: already generating — ignoring duplicate call");
      return;
    }
    if (!confirm("Rewrite the final chapter from the same direction?")) return;
    setRegenerating(true);
    try {
      applyDoneChapter(await streamChapter("regenerate"));
      toast.success("A different version, from the same cue.");
    } catch (err) {
      toast.error(err.message || "Regeneration failed");
    } finally {
      setRegenerating(false);
      setStreamingText("");
      setStreamingIndex(null);
      setStreamMode(null);
    }
  };

  // Auto-scroll to top of newest chapter on arrival
  useEffect(() => {
    if (!data || editingId) return;
    const count = data.chapters?.length || 0;
    const isFirstLoad = prevChapterCountRef.current === 0 && count > 0;
    const isNewChapter = count > prevChapterCountRef.current && prevChapterCountRef.current > 0;
    if (isFirstLoad || isNewChapter) {
      const latest = data.chapters[count - 1];
      const el = chapterRefs.current[latest.id];
      if (el) {
        requestAnimationFrame(() => {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: isFirstLoad ? "auto" : "smooth" });
        });
      }
    }
    prevChapterCountRef.current = count;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.chapters?.length]);

  useEffect(() => {
    if (generating && streamingRef.current && !streamingText) {
      const y = streamingRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [generating, streamingText]);

  // Per-chapter reading progress
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const viewportMid = window.innerHeight * 0.35;
      const next = {};
      Object.entries(chapterRefs.current).forEach(([cid, el]) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const total = rect.height;
        if (total <= 0) return;
        const consumed = viewportMid - rect.top;
        const p = Math.max(0, Math.min(1, consumed / total));
        next[cid] = p;
      });
      setChapterProgress(next);
      raf = 0;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [data?.chapters?.length]);

  // Listen for text selections inside a chapter and open the rewrite popover.
  useEffect(() => {
    const onSelect = (e) => {
      // If the event originated inside the popover / dialog / input, leave state alone.
      // (Clicking the custom-instruction field or preset button collapses the browser
      // selection, which would otherwise dismiss the popover.)
      const tgt = e && e.target;
      if (tgt && tgt.closest && (tgt.closest('[data-rewrite-popover]') || tgt.closest('[role="dialog"]'))) {
        return;
      }
      // Defer slightly so the browser's own selection has settled.
      setTimeout(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) { setSelectionAnchor(null); return; }
        const raw = sel.toString();
        const text = raw.trim();
        if (!text || text.length < 3 || text.length > 600) { setSelectionAnchor(null); return; }
        let range;
        try { range = sel.getRangeAt(0); } catch { setSelectionAnchor(null); return; }
        const startNode = range.startContainer;
        const el = startNode.nodeType === 3 ? startNode.parentElement : startNode;
        if (!el) { setSelectionAnchor(null); return; }
        // Skip if user is editing (textarea) or inside the popover/dialog itself
        if (el.closest('textarea') || el.closest('[data-rewrite-popover]') || el.closest('[role="dialog"]')) {
          return;
        }
        const chapterEl = el.closest('[data-rewrite-chapter]');
        if (!chapterEl) { setSelectionAnchor(null); return; }
        const rect = range.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) { setSelectionAnchor(null); return; }
        setSelectionAnchor({
          chapterId: chapterEl.getAttribute('data-rewrite-chapter'),
          text,
          x: rect.left + rect.width / 2,
          top: rect.top,
          bottom: rect.bottom,
        });
        setShowCustomField(false);
        setCustomInstruction("");
      }, 10);
    };
    document.addEventListener('mouseup', onSelect);
    document.addEventListener('touchend', onSelect);
    return () => {
      document.removeEventListener('mouseup', onSelect);
      document.removeEventListener('touchend', onSelect);
    };
  }, []);

  const requestRewrite = async (instructionKey, instructionText, label) => {
    if (!selectionAnchor) return;
    const { chapterId, text } = selectionAnchor;
    setRewriteBusy(instructionKey);
    try {
      const r = await api.post(
        `/stories/${id}/chapters/${chapterId}/rewrite-selection`,
        { selected_text: text, instruction: instructionText }
      );
      setRewriteResult({
        chapterId,
        original: r.data.original_passage,
        rewritten: r.data.rewritten_passage,
        newContent: r.data.new_content,
        instructionLabel: label,
      });
      setSelectionAnchor(null);
      window.getSelection()?.removeAllRanges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rewrite failed");
    } finally {
      setRewriteBusy(null);
    }
  };

  const applyRewrite = async () => {
    if (!rewriteResult) return;
    setSavingRewrite(true);
    try {
      await api.patch(
        `/stories/${id}/chapters/${rewriteResult.chapterId}`,
        { content: rewriteResult.newContent }
      );
      await load();
      setRewriteResult(null);
      toast.success("Passage rewritten.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save rewrite");
    } finally {
      setSavingRewrite(false);
    }
  };

  const startEdit = (ch) => { setEditingId(ch.id); setEditBuffer(ch.content); };
  const cancelEdit = () => { setEditingId(null); setEditBuffer(""); };
  const saveEdit = async (chapterId) => {
    if (!editBuffer.trim()) { toast.error("Cannot be empty"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/stories/${id}/chapters/${chapterId}`, { content: editBuffer });
      await load();
      setEditingId(null);
      toast.success("Chapter edited.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSavingEdit(false); }
  };

  const addBibleItem = async () => {
    const v = bibleInput.trim();
    if (!v) return;
    const next = [...(data.story.bible || []), v];
    setSavingBible(true);
    try {
      await api.patch(`/stories/${id}/bible`, { bible: next });
      await load();
      setBibleInput("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingBible(false); }
  };

  const removeBibleItem = async (idx) => {
    const next = (data.story.bible || []).filter((_, i) => i !== idx);
    setSavingBible(true);
    try {
      await api.patch(`/stories/${id}/bible`, { bible: next });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingBible(false); }
  };

  // Toggle whether a Bible entry is "known" by a specific character. Entries default to
  // global (everyone knows). Adding a character makes the entry scoped to that explicit list.
  // Clicking the only scoped char removes the scope (back to everyone).
  const toggleBibleScope = async (idx, charId) => {
    const arr = [...(data.story.bible || [])];
    const raw = arr[idx];
    const existing = (raw && typeof raw === "object") ? raw : { text: String(raw || ""), scope_character_ids: [] };
    const scope = existing.scope_character_ids || [];
    let nextScope;
    if (scope.includes(charId)) {
      nextScope = scope.filter(c => c !== charId);
    } else {
      nextScope = [...scope, charId];
    }
    arr[idx] = nextScope.length > 0
      ? { text: existing.text, scope_character_ids: nextScope }
      : existing.text;
    setSavingBible(true);
    try {
      await api.patch(`/stories/${id}/bible`, { bible: arr });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingBible(false); }
  };

  const addAvoidPhrase = async () => {
    const v = avoidPhraseInput.trim();
    if (!v) return;
    const current = data.story.avoid_phrases || [];
    if (current.some(p => p.toLowerCase() === v.toLowerCase())) {
      setAvoidPhraseInput("");
      return;
    }
    const next = [...current, v];
    setSavingAvoidPhrases(true);
    try {
      await api.patch(`/stories/${id}/avoid-phrases`, { avoid_phrases: next });
      await load();
      setAvoidPhraseInput("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingAvoidPhrases(false); }
  };

  const removeAvoidPhrase = async (idx) => {
    const next = (data.story.avoid_phrases || []).filter((_, i) => i !== idx);
    setSavingAvoidPhrases(true);
    try {
      await api.patch(`/stories/${id}/avoid-phrases`, { avoid_phrases: next });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingAvoidPhrases(false); }
  };

  // Auto-detected phrases (loaded on-demand when Bible sheet opens).
  const loadDetectedPhrases = async () => {
    setLoadingDetected(true);
    try {
      const r = await api.get(`/stories/${id}/detected-phrases`);
      setDetectedPhrases(r.data?.detected || []);
    } catch (err) {
      // Silent — not user-facing critical.
    } finally {
      setLoadingDetected(false);
    }
  };

  // Move a detected phrase into the manual ban list.
  const banDetectedPhrase = async (phrase) => {
    const current = data.story.avoid_phrases || [];
    if (current.some(p => p.toLowerCase() === phrase.toLowerCase())) return;
    const next = [...current, phrase];
    try {
      await api.patch(`/stories/${id}/avoid-phrases`, { avoid_phrases: next });
      await Promise.all([load(), loadDetectedPhrases()]);
      toast.success(`Banned: "${phrase}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  // Whitelist a detected phrase so it's no longer auto-suppressed.
  const allowDetectedPhrase = async (phrase) => {
    const current = data.story.allowed_phrases || [];
    if (current.some(p => p.toLowerCase() === phrase.toLowerCase())) return;
    const next = [...current, phrase];
    try {
      await api.patch(`/stories/${id}/allowed-phrases`, { allowed_phrases: next });
      await Promise.all([load(), loadDetectedPhrases()]);
      toast.success(`Allowed: "${phrase}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const unallowPhrase = async (idx) => {
    const next = (data.story.allowed_phrases || []).filter((_, i) => i !== idx);
    try {
      await api.patch(`/stories/${id}/allowed-phrases`, { allowed_phrases: next });
      await Promise.all([load(), loadDetectedPhrases()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  // Cast view: load per-character state for a chapter.
  const loadCastState = async (chapterId, { refresh = false } = {}) => {
    setCastState(prev => ({ ...prev, [chapterId]: { ...(prev[chapterId] || {}), loading: true } }));
    try {
      const r = await api.get(`/stories/${id}/chapters/${chapterId}/cast-state${refresh ? "?refresh=true" : ""}`);
      setCastState(prev => ({ ...prev, [chapterId]: { ...(prev[chapterId] || {}), loading: false, cast: r.data?.cast || [] } }));
    } catch (err) {
      setCastState(prev => ({ ...prev, [chapterId]: { ...(prev[chapterId] || {}), loading: false } }));
      toast.error(err.response?.data?.detail || "Could not load cast");
    }
  };

  const toggleCastView = (chapterId) => {
    const current = castState[chapterId];
    const opening = !current?.open;
    setCastState(prev => ({ ...prev, [chapterId]: { ...(current || {}), open: opening } }));
    if (opening && !current?.cast) {
      loadCastState(chapterId);
    }
  };

  const setStoryReplyCount = async (newCount) => {
    setSavingReplyCount(true);
    try {
      await api.patch(`/stories/${id}/chat-reply-count`, { chat_reply_count: newCount });
      await load();
      toast.success(`Default: ${newCount}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingReplyCount(false); }
  };

  const setStoryBeatLength = async (newLen) => {
    setSavingBeatLength(true);
    try {
      await api.patch(`/stories/${id}/beat-length`, { beat_length: newLen });
      await load();
      toast.success(`Beat length: ${newLen}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingBeatLength(false); }
  };

  const saveAvoidNote = async () => {
    if (avoidDraft === null) return;
    setSavingAvoid(true);
    try {
      await api.patch(`/stories/${id}/avoid-note`, { avoid_note: avoidDraft });
      await load();
      setAvoidDraft(null);
      toast.success("Do-not list updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingAvoid(false); }
  };

  const toggleEmotionalArc = async () => {
    const current = !!story?.no_emotional_arc;
    setSavingEmotional(true);
    try {
      await api.patch(`/stories/${id}/emotional-arc`, { no_emotional_arc: !current });
      await load();
      toast.success(current ? "Emotional arc re-enabled." : "Kept it physical.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingEmotional(false); }
  };

  const [savingIntensity, setSavingIntensity] = useState(false);
  const setStoryIntensity = async (newTier) => {
    if (!newTier || newTier === story?.intensity) return;
    setSavingIntensity(true);
    try {
      await api.patch(`/stories/${id}/intensity`, { intensity: newTier });
      await load();
      toast.success(`Intensity: ${newTier}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingIntensity(false); }
  };

  const saveCanon = async (chapterId) => {
    try {
      await api.patch(`/stories/${id}/chapters/${chapterId}/canon`, { canon: canonBuffer });
      await load();
      setEditingCanonId(null);
      toast.success("Snapshot updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const regenerateCanon = async (chapterId) => {
    setRegeneratingCanonId(chapterId);
    try {
      await api.post(`/stories/${id}/chapters/${chapterId}/regenerate-canon`);
      await load();
      toast.success("Snapshot re-summarized");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not re-summarize");
    } finally {
      setRegeneratingCanonId(null);
    }
  };

  const rewindTo = async (chapter) => {
    try {
      const r = await api.post(`/stories/${id}/rewind/${chapter.id}`);
      await load();
      toast.success(`Rewound. ${r.data.deleted} chapter${r.data.deleted === 1 ? "" : "s"} removed.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rewind failed");
    }
  };

  if (loading) return <div className="min-h-screen pt-32 px-6 text-stone-500 font-serif-reading italic">Opening the book…</div>;
  if (!data) return <div className="min-h-screen pt-32 px-6 text-stone-500">Not found.</div>;

  const { story, chapters, characters, theme } = data;
  const lastChapter = chapters[chapters.length - 1];
  const isChat = story?.mode === "chat";
  const chapterMode = (ch) => (ch?.mode === "chat" || ch?.mode === "prose" || ch?.mode === "scene") ? ch.mode : (story?.mode || "prose");
  const playerChar = (characters || []).find(c => c.id === story?.chat_player_id);
  const playerName = playerChar?.name || "";
  const aiChars = (characters || []).filter(c => c.id !== story?.chat_player_id);
  // Active AI characters in the CURRENT open chat/scene chapter. Defaults to all AI chars when
  // the chapter has no presence list (legacy chapters / brand-new scene before any toggle).
  const lastChapterActiveIds = lastChapter?.active_character_ids;
  const activeAiCharIds = (lastChapterActiveIds && lastChapterActiveIds.length > 0)
    ? new Set(lastChapterActiveIds)
    : null; // null = "everyone is active"
  const activeAiChars = activeAiCharIds
    ? (lastChapterActiveIds.map(cid => aiChars.find(c => c.id === cid)).filter(Boolean))
    : aiChars;
  const offstageAiChars = activeAiCharIds ? aiChars.filter(c => !activeAiCharIds.has(c.id)) : [];
  // Order for rendering the presence-pill row: active (in priority order) first, then off-stage.
  const orderedPanelChars = [...activeAiChars, ...offstageAiChars];
  const replyMode = story?.chat_reply_mode === "directed" ? "directed" : "auto";
  // Composer mode: explicit override > last chapter's mode > story.mode
  const lastChapterMode = lastChapter ? chapterMode(lastChapter) : (story?.mode || "prose");
  // Drift health scan — only for open chat/scene chapters where Fix Drift is applicable.
  // Cheap enough to run on every render (single-pass regex scan, no API call).
  const driftHealth = (lastChapter?.is_open && (lastChapterMode === "scene" || lastChapterMode === "chat"))
    ? detectSceneDrift(lastChapter.content || "")
    : null;
  const composerEffectiveMode = composerMode || lastChapterMode;
  // Scene-state helpers (chat composer only)
  // True when there's an open chat OR scene chapter — the "End the scene" button
  // surfaces on the composer in either case. Closing the scene generates a canon
  // snapshot so the AI gets a clean break, then the next message starts a new chapter.
  // (Bug fix: this used to require `composerEffectiveMode === "chat"`, so scene mode
  // had NO way to close a scene from the composer and every conversation became one
  // infinitely-long scene — which is why the AI kept "forgetting" location/time.)
  const currentSceneOpen =
    (composerEffectiveMode === "chat" || composerEffectiveMode === "scene")
    && lastChapter?.is_open === true
    && (lastChapterMode === "chat" || lastChapterMode === "scene");
  const playerTurnsInScene = (() => {
    if (!currentSceneOpen || !playerName) return 0;
    const re = new RegExp(`^\\s*${playerName.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}\\s*:`, "gim");
    return ((lastChapter.content || "").match(re) || []).length;
  })();
  const sceneNudge = currentSceneOpen && playerTurnsInScene >= 30;
  // Mixed-mode capability for this story. Two cases:
  // 1) Story started in chat mode → chat_player_id already set → can switch freely.
  // 2) Story started in prose mode → user can opt into texting mid-story by picking
  //    a player character (PATCH /stories/{id}/chat-player). Requires 2–4 chars.
  const charCount = story?.character_ids?.length || 0;
  // Texting requires chat-mode permission + a chat-eligible cast size (2–4).
  const canMix = !!user?.mixed_mode_enabled && !!user?.chat_mode_enabled && charCount >= 2 && charCount <= 4;
  // Scene mode requires scene-mode permission + a chat-eligible cast size (2–4).
  const canScene = !!user?.mixed_mode_enabled && !!user?.scene_mode_enabled && charCount >= 2 && charCount <= 4;
  // Show the format toggle bar when ANY non-default mode is available.
  const canShowFormatToggle = canMix || canScene;
  // The player picker is required when the story was started in prose (no chat_player_id)
  // AND the user is eligible for EITHER chat or scene mode. The picker target (chat vs scene)
  // is captured per-click on the format toggle so the composer switches to the right mode.
  const needsPlayerPick = (canMix || canScene) && !story?.chat_player_id;

  // Face-to-Face: derived data (used by panel + bubble buttons)
  // TEMPORARILY HIDDEN — flip to true to re-enable. Voice-only TTS still works.
  const F2F_FEATURE_ENABLED = false;
  const f2fEligible = F2F_FEATURE_ENABLED && !!user?.lipsync_enabled && isChat && aiChars.some(c => !!c.portrait_url);
  const f2fSpeaker = aiChars.find(c => !!c.portrait_url) || null;
  const f2fSpeakerPortrait = f2fSpeaker?.portrait_url || "";

  const formatContent = (text) =>
    text.split(/\n\n+/).filter(Boolean).map((p, i) => <p key={i}>{p.trim()}</p>);

  // Per-character tone map for the cast — keyed by uppercase first name.
  // Computed inline (no hook — runs after the loading guard above).
  const sceneToneMap = buildSceneToneMap(data?.characters || []);

  // Screenplay-format renderer for scene-mode chapters.
  // Layout per the user's reference:
  //   (Name)        ← parenthesized speaker label in the character's tone color
  //       *action*  ← small italic action lines, indented under the speaker
  //       Dialogue. ← main reading-size dialogue, indented under the speaker
  // Player's beats right-align; AI beats left-align. Pure narration stays centered.
  const formatSceneContent = (text, opts = {}) => {
    const { chapterId = "stream", showCaret = false, canRewind = false, canRegenerateLast = false, isStreaming = false } = opts;
    let beats = parseSceneContent(text);
    const playerUpper = (playerName || "").trim().split(" ")[0].toUpperCase();
    // ── Streaming-only filters ──────────────────────────────────────────────
    // The backend post-strips player lines (from AI output), former-cast lines, and truncates
    // to a beat cap BEFORE persisting the chapter. Without matching filters here, those lines
    // briefly render mid-stream and then vanish on `done`, leaving surviving AI replies that
    // appear to answer nothing. We mirror the backend's cleanup as tokens arrive.
    //
    // Nuance: the very FIRST token in a scene continue-stream is the player's echo
    // (e.g. `KATE: hi`) — that one should render. Only SUBSEQUENT player-labeled beats are
    // AI hallucinations. Same principle for former-cast: their historical lines live in
    // `scene_so_far` (rendered in prior chapter cards, not in `streamingText`), so any
    // former-cast label appearing in `streamingText` is by definition a new AI hallucination.
    if (isStreaming) {
      const currentCastFirsts = new Set(
        (characters || [])
          .map(c => (c.name || "").trim().split(" ")[0].toUpperCase())
          .filter(Boolean)
      );
      const forbiddenFirsts = new Set((data?.forbidden_speakers || []).map(n => (n || "").trim().split(" ")[0].toUpperCase()));
      let npcLines = 0;
      let sawPlayerEcho = false;
      const filtered = [];
      for (const b of beats) {
        if (b.type !== "dialogue") { filtered.push(b); continue; }
        const spk = (b.speaker || "").toUpperCase();
        // Reserved labels (narration, scene, time) always pass through.
        if (spk === "NARRATION" || spk === "SCENE" || spk === "TIME") { filtered.push(b); continue; }
        const spkFirst = spk.trim().split(/\s+/)[0];
        if (playerUpper && (spk === playerUpper || spkFirst === playerUpper.split(" ")[0])) {
          // Only a player line that OPENS the stream is the echo of what the reader typed.
          // Anywhere else the AI is ventriloquizing the player — the backend cuts the reply
          // there (everything after reacts to words the reader never said), so stop here too.
          if (!sawPlayerEcho && filtered.length === 0) {
            sawPlayerEcho = true;
            filtered.push(b);
            continue;
          }
          break;
        }
        // Former / off-stage cast are hidden (backend strips them). Other unknown speakers are
        // walk-ons: the backend keeps the first 2 per turn.
        if (forbiddenFirsts.has(spkFirst)) continue;
        if (!currentCastFirsts.has(spkFirst)) {
          npcLines += 1;
          if (npcLines > (story?.walk_ons_enabled === false ? 0 : 2)) continue;
        }
        filtered.push(b);
      }
      beats = filtered;
      // Truncate to the same beat cap the backend applies (short=3 / medium=6 / long=10).
      // Note: this cap is applied by the backend to the AI's output ONLY (not counting the
      // player echo), so we do the same here — count only beats that came AFTER the echo.
      const cap = (() => {
        const bl = (turnBeatLength || story?.beat_length || "medium").toLowerCase();
        if (bl === "short") return 3;
        if (bl === "long") return 10;
        return 6;
      })();
      if (cap > 0) {
        const capped = [];
        let aiBeatCount = 0;
        let pastEcho = !sawPlayerEcho; // if there's no echo, every AI beat counts from the start
        for (const b of beats) {
          if (!pastEcho && b.type === "dialogue" && (b.speaker || "").toUpperCase() === playerUpper) {
            capped.push(b);
            pastEcho = true;
            continue;
          }
          if (aiBeatCount >= cap) break;
          capped.push(b);
          // Only DIALOGUE + ACTION count toward the cap (matches `_truncate_scene_to_cap`).
          if (b.type === "dialogue" || b.type === "action") aiBeatCount++;
        }
        beats = capped;
      }
    }
    // Find the index of the LAST AI beat (used for the regenerate-last button)
    let lastAiBeatIdx = -1;
    for (let k = beats.length - 1; k >= 0; k--) {
      const bb = beats[k];
      if (bb.type === "dialogue" && playerUpper && bb.speaker !== playerUpper) {
        lastAiBeatIdx = k;
        break;
      }
    }
    return (
      <div className="space-y-5" data-testid={`scene-content-${chapterId}`}>
        {beats.map((b, i) => {
          const last = i === beats.length - 1;
          if (b.type === "action") {
            // Centered narration / orphan action line (between dialogue beats)
            const showActionRewind = canRewind && i >= 1 && !showCaret;
            return (
              <div key={`${chapterId}-${i}`} className="text-center py-1.5 group" data-testid={`scene-beat-${i}`}>
                <div className="inline-flex items-center gap-2">
                  <span className="inline-block font-sans-ui italic text-[13px] tracking-wide text-stone-500 max-w-[88%]">
                    *{b.text}*
                    {last && showCaret && <span className="inline-block w-1.5 h-3 bg-rose-800 ml-1 align-baseline animate-pulse" />}
                  </span>
                  {showActionRewind && (
                    <button
                      type="button"
                      data-testid={`scene-rewind-btn-${i}`}
                      onClick={() => rewindChatToBeat(chapterId, i, b.anchor, b.lineIdx)}
                      title="Rewind to before this beat"
                      disabled={generating || regenerating}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition inline-flex items-center justify-center w-6 h-6 rounded-full border border-stone-700 bg-stone-900 text-stone-500 hover:text-rose-300 hover:border-rose-800 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Rewind className="w-3 h-3" strokeWidth={1.8} />
                    </button>
                  )}
                </div>
              </div>
            );
          }
          const isPlayer = playerUpper && b.speaker === playerUpper;
          const tone = sceneToneMap[b.speaker] || (isPlayer ? 8 : 1);
          const segs = b.segments || [];
          // Find index of the LAST dialogue segment to attach the streaming caret correctly
          let lastDialogueIdx = -1;
          for (let k = segs.length - 1; k >= 0; k--) {
            if (segs[k].type === "dialogue") { lastDialogueIdx = k; break; }
          }
          // Voice playback (TTS / lipsync) — only on AI beats with text, when the feature is enabled.
          const speakerChar = !isPlayer
            ? (data?.characters || []).find(c => (c.name || "").trim().split(" ")[0].toUpperCase() === b.speaker)
            : null;
          const speakerVoiceId = speakerChar?.voice_id || "";
          const bubbleKey = `${chapterId}:${i}`;
          const isThisLoading = voiceState.key === bubbleKey && voiceState.status === "loading";
          const isThisPlaying = voiceState.key === bubbleKey && voiceState.status === "playing";
          const voiceEnabled = !!user?.lipsync_enabled;
          const showVoiceBtn = voiceEnabled && !isPlayer && !showCaret && segs.some(s => s.type === "dialogue");
          const showSceneRewind = canRewind && i >= 1 && !showCaret;
          const showSceneRegen = canRegenerateLast && !showCaret && !isPlayer && i === lastAiBeatIdx;
          // Concatenated dialogue text (skipping action segments) for TTS playback.
          const speakableText = segs.filter(s => s.type === "dialogue").map(s => s.text).join(" ");
          return (
            <div
              key={`${chapterId}-${i}`}
              data-testid={`scene-beat-${i}`}
              className={`group flex flex-col ${isPlayer ? "items-end text-right" : "items-start text-left"}`}
            >
              <div className={`font-sans-ui text-sm tracking-wide mb-1 scene-tone-${tone}-name flex items-center gap-2 ${isPlayer ? "flex-row-reverse" : ""}`}>
                <span>({b.speaker.charAt(0) + b.speaker.slice(1).toLowerCase()})</span>
                {showVoiceBtn && (
                  <button
                    type="button"
                    data-testid={`scene-voice-play-btn-${i}`}
                    onClick={() => {
                      if (!speakerVoiceId) {
                        toast.info(`No voice set for ${speakerChar?.name || b.speaker}. Add a voice ID in Characters.`);
                        return;
                      }
                      playVoice(bubbleKey, speakableText, speakerVoiceId, {
                        portraitUrl: speakerChar?.portrait_url || "",
                        portraitId: speakerChar?.id || "",
                      });
                    }}
                    title={speakerVoiceId ? (isThisPlaying ? "Stop" : "Play voice") : `No voice set for ${speakerChar?.name || b.speaker}`}
                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full border transition-colors ${
                      isThisPlaying
                        ? "bg-rose-800 border-rose-700 text-rose-50"
                        : speakerVoiceId
                          ? "bg-stone-900/60 border-stone-700 text-stone-400 hover:text-rose-200 hover:border-rose-800"
                          : "bg-stone-900/40 border-stone-800 text-stone-600"
                    }`}
                  >
                    {isThisLoading ? (
                      <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.8} />
                    ) : isThisPlaying ? (
                      <Pause className="w-3 h-3" strokeWidth={1.8} />
                    ) : (
                      <Volume2 className="w-3 h-3" strokeWidth={1.8} />
                    )}
                  </button>
                )}
                {showSceneRewind && (
                  <button
                    type="button"
                    data-testid={`scene-rewind-btn-${i}`}
                    onClick={() => rewindChatToBeat(chapterId, i, b.anchor, b.lineIdx)}
                    title="Rewind to before this beat"
                    disabled={generating || regenerating}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition inline-flex items-center justify-center w-6 h-6 rounded-full border border-stone-700 bg-stone-900 text-stone-500 hover:text-rose-300 hover:border-rose-800 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Rewind className="w-3 h-3" strokeWidth={1.8} />
                  </button>
                )}
                {showSceneRegen && (
                  <button
                    type="button"
                    data-testid={`scene-regenerate-last-btn`}
                    onClick={regenerateLast}
                    title="Rewrite the last beat from the same direction"
                    disabled={generating || regenerating}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition inline-flex items-center justify-center w-6 h-6 rounded-full border border-stone-700 bg-stone-900 text-stone-500 hover:text-rose-300 hover:border-rose-800 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-3 h-3 ${regenerating ? "animate-spin" : ""}`} strokeWidth={1.8} />
                  </button>
                )}
              </div>
              <div className={`pl-6 sm:pl-10 ${isPlayer ? "pr-0 pl-0 sm:pl-0 pr-6 sm:pr-10" : ""} space-y-1.5`}>
                {segs.map((s, j) => {
                  if (s.type === "action") {
                    return (
                      <div key={`${chapterId}-${i}-${j}`} className="font-sans-ui italic text-[13px] tracking-wide text-stone-500">
                        *{s.text}*
                      </div>
                    );
                  }
                  const isLastDialogue = j === lastDialogueIdx;
                  return (
                    <div
                      key={`${chapterId}-${i}-${j}`}
                      className={`inline-block max-w-[88%] font-serif-reading text-lg md:text-xl leading-relaxed px-3.5 py-2 rounded-2xl ${
                        isPlayer ? "rounded-tr-sm" : "rounded-tl-sm"
                      } scene-tone-${tone}-bub scene-tone-${tone}-txt`}
                    >
                      {renderInlineItalics(s.text, `${chapterId}-${i}-${j}`)}
                      {last && isLastDialogue && showCaret && (
                        <span className="inline-block w-1.5 h-4 bg-rose-800 ml-1 align-baseline animate-pulse" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const formatChatContent = (text, opts = {}) => {
    const beats = parseChatContent(text);
    const { showCaret = false, showTyping = false, chapterId = "stream", canRewind = false, canRegenerateLast = false } = opts;
    const aiName = isChat ? (characters || []).find(c => c.id !== story.chat_player_id)?.name || "" : "";
    const voiceEnabled = !!user?.lipsync_enabled;
    // Index of the last AI bubble (used for the "regenerate last" button)
    let lastAiIdx = -1;
    for (let k = beats.length - 1; k >= 0; k--) {
      const bb = beats[k];
      if (bb.type === "msg" && playerName && bb.sender.toLowerCase() !== playerName.toLowerCase()) {
        lastAiIdx = k;
        break;
      }
    }
    return (
      <div className="space-y-2.5" data-testid="chat-bubbles">
        {beats.map((b, i) => {
          const last = i === beats.length - 1;
          if (b.type === "narration") {
            return (
              <div key={i} className="text-center py-2 group" data-testid={`chat-msg-${i}`}>
                <div className="inline-flex items-center gap-2">
                  <div className="inline-block font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500 bg-stone-900/60 px-3 py-1 rounded-full">
                    {b.text}
                    {last && showCaret && <span className="inline-block w-1.5 h-3 bg-rose-800 ml-1 align-baseline animate-pulse" />}
                  </div>
                  {canRewind && i >= 1 && !showCaret && (
                    <button
                      type="button"
                      data-testid={`rewind-btn-${i}`}
                      onClick={() => rewindChatToBeat(chapterId, i)}
                      title="Rewind to before this beat"
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition inline-flex items-center justify-center w-6 h-6 rounded-full border border-stone-700 bg-stone-900 text-stone-500 hover:text-rose-300 hover:border-rose-800"
                    >
                      <Rewind className="w-3 h-3" strokeWidth={1.8} />
                    </button>
                  )}
                </div>
              </div>
            );
          }
          const isPlayer = playerName && b.sender.toLowerCase() === playerName.toLowerCase();
          // Voice playback button: only on AI bubbles when feature is enabled
          const speakerChar = !isPlayer ? (characters || []).find(c => (c.name || "").toLowerCase() === (b.sender || "").toLowerCase()) : null;
          const speakerVoiceId = speakerChar?.voice_id || "";
          const bubbleKey = `${chapterId}:${i}`;
          const isThisLoading = voiceState.key === bubbleKey && voiceState.status === "loading";
          const isThisPlaying = voiceState.key === bubbleKey && voiceState.status === "playing";
          const showVoiceBtn = voiceEnabled && !isPlayer && !showCaret && !!b.text;
          const showRewind = canRewind && i >= 1 && !showCaret;
          const showRegenAtBubble = canRegenerateLast && !showCaret && i === lastAiIdx && !isPlayer;
          return (
            <div key={i} className={`flex group ${isPlayer ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
              <div className={`max-w-[80%] ${isPlayer ? "items-end" : "items-start"} flex flex-col`}>
                <div className={`font-sans-ui text-[9px] uppercase tracking-[0.22em] mb-1 px-1 ${isPlayer ? "text-rose-400 text-right" : "text-stone-500"}`}>
                  {b.sender}
                </div>
                <div className={`flex items-end gap-1.5 ${isPlayer ? "flex-row-reverse" : ""}`}>
                  <div className={`px-4 py-2.5 rounded-2xl font-serif-reading text-base leading-relaxed ${
                    isPlayer
                      ? "bg-rose-900/70 text-rose-50 rounded-br-sm"
                      : "bg-stone-800/80 text-stone-100 rounded-bl-sm"
                  }`}>
                    {b.text}
                    {last && showCaret && <span className="inline-block w-1.5 h-3.5 bg-rose-300 ml-1 align-baseline animate-pulse" />}
                  </div>
                  {showVoiceBtn && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!speakerVoiceId) {
                          toast.info(`No voice set for ${b.sender}. Add a voice ID in Characters.`);
                          return;
                        }
                        playVoice(bubbleKey, b.text, speakerVoiceId, {
                          portraitUrl: speakerChar?.portrait_url || "",
                          portraitId: speakerChar?.id || "",
                        });
                      }}
                      data-testid={`voice-play-btn-${i}`}
                      title={speakerVoiceId ? (isThisPlaying ? "Stop" : "Play voice") : `No voice set for ${b.sender}`}
                      className={`mb-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full border transition-colors ${
                        isThisPlaying
                          ? "bg-rose-800 border-rose-700 text-rose-50"
                          : speakerVoiceId
                            ? "bg-stone-900 border-stone-700 text-stone-300 hover:text-rose-200 hover:border-rose-800"
                            : "bg-stone-900/50 border-stone-800 text-stone-600"
                      }`}
                    >
                      {isThisLoading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />
                      ) : isThisPlaying ? (
                        <Pause className="w-3.5 h-3.5" strokeWidth={1.8} />
                      ) : (
                        <Volume2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                      )}
                    </button>
                  )}
                  {showRewind && (
                    <button
                      type="button"
                      data-testid={`rewind-btn-${i}`}
                      onClick={() => rewindChatToBeat(chapterId, i)}
                      title="Rewind to before this beat"
                      disabled={generating || regenerating}
                      className="mb-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition inline-flex items-center justify-center w-7 h-7 rounded-full border border-stone-700 bg-stone-900 text-stone-500 hover:text-rose-300 hover:border-rose-800 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Rewind className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </button>
                  )}
                  {showRegenAtBubble && (
                    <button
                      type="button"
                      data-testid={`regenerate-last-bubble-btn`}
                      onClick={regenerateLast}
                      disabled={generating || regenerating}
                      title="Try a different reply"
                      className="mb-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition inline-flex items-center justify-center w-7 h-7 rounded-full border border-stone-700 bg-stone-900 text-stone-500 hover:text-rose-300 hover:border-rose-800 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {showTyping && (
          <div className="flex justify-start" data-testid="chat-typing-indicator">
            <div className="max-w-[80%] flex flex-col items-start">
              <div className="font-sans-ui text-[9px] uppercase tracking-[0.22em] mb-1 px-1 text-stone-500">
                {aiName || "..."}
              </div>
              <div className="px-4 py-3 rounded-2xl bg-stone-800/80 rounded-bl-sm flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-stone-400 typing-dot" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-stone-400 typing-dot" style={{ animationDelay: "180ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-stone-400 typing-dot" style={{ animationDelay: "360ms" }} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const charsSig = (characters || []).map(c => `${c.id}:${c.name}:${c.voice_id || ""}:${c.portrait_url || ""}`).join("|");
  const bodyDepsStatic = [voiceState, !!user?.lipsync_enabled, playerName, charsSig, isChat, story?.chat_player_id];
  const renderChapterBody = (ch, isLastIdx) => {
    const mode = chapterMode(ch);
    const canAct = ch.is_open === true && isLastIdx;
    // Only the actionable (open, last) chapter shows buttons that depend on generating state.
    const bodyDeps = canAct ? [...bodyDepsStatic, generating, regenerating] : bodyDepsStatic;
    const hit = bodyCacheRef.current.get(ch.id);
    if (hit && hit.content === ch.content && hit.mode === mode && hit.canAct === canAct && hit.deps.length === bodyDeps.length && hit.deps.every((d, i) => d === bodyDeps[i])) {
      return hit.el;
    }
    const opts = { chapterId: ch.id, canRewind: canAct, canRegenerateLast: canAct };
    const el = mode === "chat"
      ? formatChatContent(ch.content, opts)
      : mode === "scene" ? formatSceneContent(ch.content, opts) : formatContent(ch.content);
    bodyCacheRef.current.set(ch.id, { content: ch.content, mode, canAct, deps: bodyDeps, el });
    return el;
  };

  return (
    <div className="min-h-screen bg-stone-950 grain-overlay pt-20 pb-32" data-testid="story-reader-page">
      {/* Sticky action bar — stays under the Navbar while scrolling */}
      <div
        className="sticky top-16 z-30 bg-stone-950/85 backdrop-blur-xl border-b border-stone-800/50"
        data-testid="reader-sticky-bar"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/dashboard" className="inline-flex items-center gap-2 font-sans-ui text-xs uppercase tracking-[0.25em] text-stone-500 hover:text-stone-200 transition" data-testid="reader-back-btn">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> My Stories
          </Link>
          {chapters.length > 0 && (
            <div className="flex items-center gap-5">
              {f2fEligible && (
                <button
                  type="button"
                  data-testid="f2f-toggle-btn"
                  onClick={() => setFaceToFaceOn(v => !v)}
                  title={faceToFaceOn ? "Hide portrait" : "Show portrait (Face-to-Face Mode)"}
                  className={`inline-flex items-center gap-2 font-sans-ui text-xs uppercase tracking-[0.25em] transition ${
                    faceToFaceOn ? "text-rose-300 hover:text-rose-100" : "text-stone-500 hover:text-stone-200"
                  }`}
                >
                  <UserCircle2 className="w-4 h-4" strokeWidth={1.5} /> Face
                </button>
              )}
              <Sheet onOpenChange={(open) => { if (open) loadDetectedPhrases(); }}>
                <SheetTrigger asChild>
                  <button data-testid="open-bible-btn" className="inline-flex items-center gap-2 font-sans-ui text-xs uppercase tracking-[0.25em] text-stone-500 hover:text-stone-200 transition">
                    <NotebookPen className="w-4 h-4" strokeWidth={1.5} /> Bible
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-stone-950 border-stone-800 text-stone-200 w-full sm:w-[420px] sm:max-w-md overflow-y-auto px-5 sm:px-6">
                  <SheetHeader>
                    <SheetTitle className="font-serif-heading text-3xl font-light text-stone-100 text-left">
                      Story Bible
                    </SheetTitle>
                  </SheetHeader>
                  <div className="space-y-10 mt-6">
                    {/* Live scene memory — what the AI is tracking RIGHT NOW for the open chat/scene chapter */}
                    {(lastChapter?.is_open && (lastChapter?.scene_memory || "").trim() && (lastChapterMode === "chat" || lastChapterMode === "scene")) && (
                      <section data-testid="reader-scene-memory">
                        <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3 flex items-center gap-2">
                          <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                          What the AI is tracking
                        </div>
                        <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                          A live snapshot the AI uses as memory for this scene. If something here is wrong, pin a fact above and the next reply will follow.
                        </p>
                        {(() => {
                          const raw = (lastChapter.scene_memory || "").trim();
                          // Parse the structured schema into sections. Each section is either:
                          //   - `HEADER: value` (LOCATION, TIME, CAST, OBJECTS)
                          //   - `HEADER:` followed by `· bullet` lines (EVENTS, COMMITMENTS, FACTS)
                          const sections = [];
                          const lines = raw.split("\n").map(l => l.trim()).filter(Boolean);
                          let current = null;
                          const HEADERS = new Set(["LOCATION", "TIME", "CAST", "EVENTS", "COMMITMENTS", "OBJECTS", "FACTS"]);
                          for (const line of lines) {
                            const m = line.match(/^([A-Z]+):\s*(.*)$/);
                            if (m && HEADERS.has(m[1])) {
                              if (current) sections.push(current);
                              current = { header: m[1], inline: m[2].trim(), bullets: [] };
                            } else if (line.startsWith("·")) {
                              if (current) current.bullets.push(line.replace(/^·\s*/, "").trim());
                            } else if (current) {
                              // continuation line — fold into the most recent bullet or inline value
                              if (current.bullets.length > 0) {
                                current.bullets[current.bullets.length - 1] += " " + line;
                              } else if (current.inline) {
                                current.inline += " " + line;
                              }
                            }
                          }
                          if (current) sections.push(current);
                          if (sections.length === 0) {
                            return (
                              <pre data-testid="scene-memory-raw" className="font-mono text-xs text-stone-400 whitespace-pre-wrap bg-stone-900/40 border border-stone-800 p-3 leading-relaxed">{raw}</pre>
                            );
                          }
                          const labelFor = (h) => ({
                            LOCATION: "Location",
                            TIME: "Time",
                            CAST: "Other people mentioned",
                            EVENTS: "What happened",
                            COMMITMENTS: "Promises / plans / off-screen actions",
                            OBJECTS: "Notable objects",
                            FACTS: "Revealed facts",
                          }[h] || h);
                          return (
                            <div className="space-y-3" data-testid="scene-memory-parsed">
                              {sections.map((s, i) => {
                                const hasContent = (s.inline && s.inline !== "(none yet)") || s.bullets.length > 0;
                                if (!hasContent) return null;
                                const isInline = ["LOCATION", "TIME", "CAST", "OBJECTS"].includes(s.header);
                                return (
                                  <div key={i} data-testid={`scene-memory-${s.header.toLowerCase()}`} className="border border-stone-800 bg-stone-900/40 p-3">
                                    <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-rose-700 mb-1.5">
                                      {labelFor(s.header)}
                                    </div>
                                    {isInline && s.inline ? (
                                      <div className="font-serif-reading text-stone-200 text-sm leading-relaxed">
                                        {s.inline}
                                      </div>
                                    ) : s.bullets.length > 0 ? (
                                      <ul className="space-y-1.5">
                                        {s.bullets.filter(b => b.toLowerCase() !== "(none yet)").map((b, bi) => (
                                          <li key={bi} className="font-serif-reading text-stone-200 text-sm leading-relaxed flex gap-2">
                                            <span className="text-rose-800 shrink-0">·</span>
                                            <span>{b}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {(lastChapter.scene_memory_at_beats ?? 0) > 0 && (
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600 mt-3">
                            Snapshot taken at beat {lastChapter.scene_memory_at_beats} · auto-refreshes as the scene grows
                          </div>
                        )}
                      </section>
                    )}

                    {(lastChapter?.is_open && !(lastChapter?.scene_memory || "").trim() && (lastChapterMode === "chat" || lastChapterMode === "scene")) && (
                      <section data-testid="reader-scene-memory-pending">
                        <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3 flex items-center gap-2">
                          <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                          What the AI is tracking
                        </div>
                        <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed">
                          The AI will build a memory snapshot of this scene once it has a bit more to track. Keep going — a snapshot usually appears within the first 10 messages.
                        </p>
                      </section>
                    )}

                    {/* Drift normalizer — show on any open chat/scene chapter for one-click cleanup */}
                    {lastChapter?.is_open && (lastChapterMode === "chat" || lastChapterMode === "scene") && (
                      <section
                        data-testid="reader-drift-normalizer"
                        className={`border p-3 ${driftHealth?.total ? "border-amber-900/60 bg-amber-950/15" : "border-stone-800 bg-stone-900/30"}`}
                      >
                        <div className={`overline text-[10px] font-sans-ui uppercase tracking-[0.3em] mb-2 flex items-center gap-2 ${driftHealth?.total ? "text-amber-400" : "text-rose-800"}`}>
                          <Wand2 className="w-3 h-3" strokeWidth={1.5} />
                          {driftHealth?.total ? "Drift detected" : "Fix scene drift"}
                          {driftHealth?.total > 0 && (
                            <span
                              data-testid="drift-health-badge-count"
                              className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px] tracking-normal font-medium"
                            >
                              {driftHealth.total}
                            </span>
                          )}
                        </div>
                        {driftHealth?.total > 0 ? (
                          <>
                            <p className="font-serif-reading italic text-stone-400 text-xs leading-relaxed mb-2">
                              Found {driftHealth.total} drift {driftHealth.total === 1 ? "pattern" : "patterns"} in this chapter:
                            </p>
                            <ul data-testid="drift-health-breakdown" className="font-sans-ui text-[11px] text-stone-400 leading-relaxed mb-3 space-y-0.5 pl-3">
                              {driftHealth.stackedSpeakers > 0 && (
                                <li>· <span className="text-amber-300">{driftHealth.stackedSpeakers}</span> stacked-speaker {driftHealth.stackedSpeakers === 1 ? "switch" : "switches"} (multiple speakers on one line)</li>
                              )}
                              {driftHealth.asteriskDialogue > 0 && (
                                <li>· <span className="text-amber-300">{driftHealth.asteriskDialogue}</span> asterisk-wrapped {driftHealth.asteriskDialogue === 1 ? "dialogue" : "dialogues"}</li>
                              )}
                              {driftHealth.parenCues > 0 && (
                                <li>· <span className="text-amber-300">{driftHealth.parenCues}</span> {driftHealth.parenCues === 1 ? "parenthetical cue" : "parenthetical cues"} (Name)</li>
                              )}
                              {driftHealth.hollywoodCues > 0 && (
                                <li>· <span className="text-amber-300">{driftHealth.hollywoodCues}</span> Hollywood-style bare {driftHealth.hollywoodCues === 1 ? "name cue" : "name cues"}</li>
                              )}
                              {driftHealth.bareNameCues > 0 && (
                                <li>· <span className="text-amber-300">{driftHealth.bareNameCues}</span> bare {driftHealth.bareNameCues === 1 ? "NAME: cue" : "NAME: cues"} with no body</li>
                              )}
                              {driftHealth.multilineWraps > 0 && (
                                <li>· <span className="text-amber-300">{driftHealth.multilineWraps}</span> multi-line asterisk {driftHealth.multilineWraps === 1 ? "wrap" : "wraps"}</li>
                              )}
                            </ul>
                          </>
                        ) : (
                          <p className="font-serif-reading italic text-stone-500 text-xs leading-relaxed mb-3">
                            If a character&apos;s dialogue is rendering as italic stage directions instead of speech bubbles, click below. The server will rewrite any `(Name)` cues, asterisk-wrapped dialogue, stacked speakers, or Hollywood-style speaker lines into canonical format. Idempotent and safe to run.
                          </p>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          data-testid="normalize-drift-btn"
                          onClick={normalizeChapterDrift}
                          disabled={normalizingDrift}
                          className={`rounded-full h-8 px-4 font-sans-ui text-[10px] uppercase tracking-[0.25em] ${driftHealth?.total ? "text-amber-200 border border-amber-700/60 hover:bg-amber-950/40" : "text-rose-300 border border-rose-900/40 hover:bg-rose-950/40"}`}
                        >
                          {normalizingDrift ? "Fixing…" : driftHealth?.total ? "Clean up drift" : "Normalize this chapter"}
                        </Button>
                      </section>
                    )}

                    {(lastChapter?.is_open && (lastChapter?.scene_memory || "").trim() && (lastChapterMode === "chat" || lastChapterMode === "scene")) && (
                      <div className="hairline"></div>
                    )}

                    {/* Bible section */}
                    <section>
                      <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3">
                        Pinned facts
                      </div>
                      <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                        Facts you want the AI to remember every chapter. &ldquo;They met tonight, at the gallery&rdquo; &mdash; &ldquo;Elena&rsquo;s left hand is bandaged&rdquo; &mdash; &ldquo;Marcus is married&rdquo;.
                      </p>
                      <div className="space-y-2 mb-4">
                        {(story.bible || []).length === 0 && (
                          <div className="font-serif-reading italic text-stone-600 text-sm">No pinned facts yet.</div>
                        )}
                        {(story.bible || []).map((b, i) => {
                          const entry = (b && typeof b === "object") ? b : { text: String(b || ""), scope_character_ids: [] };
                          const scope = entry.scope_character_ids || [];
                          const isGlobal = scope.length === 0;
                          return (
                            <div key={i} data-testid={`bible-item-${i}`} className="p-3 border border-stone-800 bg-stone-900/40 space-y-2">
                              <div className="flex items-start justify-between gap-3">
                                <span className="font-serif-reading text-stone-200 text-sm leading-relaxed">{entry.text}</span>
                                <button
                                  data-testid={`bible-remove-${i}`}
                                  onClick={() => removeBibleItem(i)}
                                  className="text-stone-600 hover:text-rose-500 transition shrink-0 mt-0.5"
                                >
                                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                </button>
                              </div>
                              {(characters || []).length >= 2 && (
                                <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-stone-800/60">
                                  <span className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600 mr-1">
                                    Known by:
                                  </span>
                                  {isGlobal && (
                                    <span data-testid={`bible-scope-global-${i}`} className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500 italic">
                                      everyone
                                    </span>
                                  )}
                                  {(characters || []).map(c => {
                                    const checked = scope.includes(c.id);
                                    if (isGlobal && !checked) {
                                      // Show muted chips so user can click to scope
                                      return (
                                        <button
                                          key={c.id}
                                          type="button"
                                          data-testid={`bible-scope-${i}-${c.id}`}
                                          onClick={() => toggleBibleScope(i, c.id)}
                                          disabled={savingBible}
                                          className="font-sans-ui text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border border-stone-800 text-stone-600 hover:border-stone-600 hover:text-stone-300 transition"
                                        >
                                          + {(c.name || "").split(" ")[0]}
                                        </button>
                                      );
                                    }
                                    if (!isGlobal) {
                                      return (
                                        <button
                                          key={c.id}
                                          type="button"
                                          data-testid={`bible-scope-${i}-${c.id}`}
                                          onClick={() => toggleBibleScope(i, c.id)}
                                          disabled={savingBible}
                                          className={`font-sans-ui text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border transition ${
                                            checked
                                              ? "border-rose-800 bg-rose-950/30 text-rose-200"
                                              : "border-stone-800 text-stone-600 hover:border-stone-600 hover:text-stone-300"
                                          }`}
                                        >
                                          {(c.name || "").split(" ")[0]}
                                        </button>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              )}
                              {!isGlobal && (
                                <div
                                  data-testid={`bible-scope-warning-${i}`}
                                  className="flex items-start gap-2 mt-1 px-2 py-1.5 bg-amber-950/20 border border-amber-900/40 rounded-sm"
                                >
                                  <span className="font-sans-ui text-amber-400 text-[10px] mt-0.5">⚠</span>
                                  <p className="font-serif-reading italic text-amber-200/80 text-[11px] leading-snug">
                                    Scoped facts only reach the AI when those characters are <strong>active</strong> in the scene. For <strong>shared knowledge</strong> (e.g. &ldquo;Jayne knows about Chris and Kate&rdquo;), keep it global — otherwise the AI will forget when Jayne&rsquo;s off-stage.
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          data-testid="bible-input"
                          value={bibleInput}
                          onChange={(e) => setBibleInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBibleItem(); } }}
                          placeholder="A fact, rule, or boundary…"
                          className="bg-stone-900 border-stone-800 rounded-none h-11 text-stone-100 font-serif-reading focus:ring-rose-900 flex-1"
                        />
                        <Button
                          data-testid="bible-add-btn"
                          onClick={addBibleItem}
                          disabled={!bibleInput.trim() || savingBible}
                          className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-11 px-4 font-sans-ui text-xs uppercase tracking-[0.25em]"
                        >
                          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </section>

                    <div className="hairline"></div>

                    {/* Do-not list */}
                    <section>
                      <div className="flex items-center justify-between mb-3">
                        <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800">
                          Do not list
                        </div>
                        {avoidDraft === null && (
                          <button
                            data-testid="edit-avoid-btn"
                            onClick={() => setAvoidDraft(story.avoid_note || "")}
                            className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500 hover:text-rose-300 transition"
                          >
                            {(story.avoid_note || "").trim() ? "Edit" : "Add"}
                          </button>
                        )}
                      </div>
                      <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                        Hard rules the AI must respect in every chapter. Words, topics, tropes to keep out.
                      </p>
                      {avoidDraft === null ? (
                        (story.avoid_note || "").trim() ? (
                          <div className="p-3 border border-stone-800 bg-stone-900/40">
                            <div data-testid="avoid-display" className="font-serif-reading text-stone-300 text-sm leading-relaxed whitespace-pre-wrap">
                              {story.avoid_note}
                            </div>
                          </div>
                        ) : (
                          <div className="font-serif-reading italic text-stone-600 text-sm">Nothing excluded yet.</div>
                        )
                      ) : (
                        <div className="space-y-2">
                          <Textarea
                            data-testid="avoid-input"
                            autoFocus
                            value={avoidDraft}
                            onChange={(e) => setAvoidDraft(e.target.value)}
                            placeholder={"No blasphemy. No pet names. No smoking."}
                            className="bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading text-sm leading-relaxed focus:ring-rose-900 min-h-[120px]"
                          />
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              disabled={savingAvoid}
                              onClick={() => setAvoidDraft(null)}
                              className="rounded-none h-9 px-3 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[11px] uppercase tracking-[0.25em]"
                            >
                              Cancel
                            </Button>
                            <Button
                              data-testid="save-avoid-btn"
                              disabled={savingAvoid}
                              onClick={saveAvoidNote}
                              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-9 px-4 font-sans-ui text-[11px] uppercase tracking-[0.25em]"
                            >
                              {savingAvoid ? "Saving…" : "Save"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </section>

                    <div className="hairline"></div>

                    {/* Phrases to avoid */}
                    <section>
                      <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3">
                        Phrases to avoid
                      </div>
                      <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                        Stock phrases or stylistic tics the AI keeps reaching for. Cross-chapter repeats are also detected
                        automatically &mdash; this list is for ones you want to ban by hand.
                      </p>
                      <div className="space-y-2 mb-4">
                        {(story.avoid_phrases || []).length === 0 && (
                          <div className="font-serif-reading italic text-stone-600 text-sm">No phrases banned yet.</div>
                        )}
                        {(story.avoid_phrases || []).map((p, i) => (
                          <div key={i} data-testid={`avoid-phrase-item-${i}`} className="flex items-start justify-between gap-3 p-3 border border-stone-800 bg-stone-900/40">
                            <span className="font-serif-reading text-stone-200 text-sm leading-relaxed">&ldquo;{p}&rdquo;</span>
                            <button
                              data-testid={`avoid-phrase-remove-${i}`}
                              onClick={() => removeAvoidPhrase(i)}
                              className="text-stone-600 hover:text-rose-500 transition shrink-0 mt-0.5"
                            >
                              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          data-testid="avoid-phrase-input"
                          value={avoidPhraseInput}
                          onChange={(e) => setAvoidPhraseInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAvoidPhrase(); } }}
                          placeholder="e.g. fair warning, sodium light…"
                          className="bg-stone-900 border-stone-800 rounded-none h-11 text-stone-100 font-serif-reading focus:ring-rose-900 flex-1"
                        />
                        <Button
                          data-testid="avoid-phrase-add-btn"
                          onClick={addAvoidPhrase}
                          disabled={!avoidPhraseInput.trim() || savingAvoidPhrases}
                          className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-11 px-4 font-sans-ui text-xs uppercase tracking-[0.25em]"
                        >
                          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </section>

                    <div className="hairline"></div>

                    {/* Auto-detected this story */}
                    <section>
                      <div className="flex items-center justify-between mb-3">
                        <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800">
                          Detected this story
                        </div>
                        <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-600">
                          {loadingDetected ? "Scanning…" : `${detectedPhrases.length} found`}
                        </div>
                      </div>
                      <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                        Phrases the AI has reached for in 2+ chapters. They&rsquo;re already being auto-suppressed.
                        <strong className="not-italic text-stone-400"> Ban</strong> sends to the list above;
                        <strong className="not-italic text-stone-400"> Allow</strong> whitelists it (the AI may use it freely).
                      </p>
                      <div className="space-y-2 mb-4">
                        {!loadingDetected && detectedPhrases.length === 0 && (
                          <div className="font-serif-reading italic text-stone-600 text-sm">
                            {(chapters || []).length < 2
                              ? "Need at least 2 chapters before detection runs."
                              : "Nothing to flag yet. Clean style so far."}
                          </div>
                        )}
                        {detectedPhrases.map((d, i) => (
                          <div key={d.phrase} data-testid={`detected-phrase-${i}`} className="flex items-start justify-between gap-3 p-3 border border-stone-800 bg-stone-900/40">
                            <div className="flex-1 min-w-0">
                              <div className="font-serif-reading text-stone-200 text-sm leading-relaxed truncate">
                                &ldquo;{d.phrase}&rdquo;
                              </div>
                              <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-500 mt-1">
                                {d.count}× across chapters
                              </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <button
                                data-testid={`detected-ban-${i}`}
                                onClick={() => banDetectedPhrase(d.phrase)}
                                className="font-sans-ui text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 border border-rose-900 bg-rose-950/30 text-rose-300 hover:bg-rose-900/40 transition"
                              >
                                Ban
                              </button>
                              <button
                                data-testid={`detected-allow-${i}`}
                                onClick={() => allowDetectedPhrase(d.phrase)}
                                className="font-sans-ui text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 border border-stone-700 bg-stone-900/40 text-stone-400 hover:text-stone-100 hover:border-stone-500 transition"
                              >
                                Allow
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {(story.allowed_phrases || []).length > 0 && (
                        <div className="mt-4">
                          <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">
                            Whitelisted ({(story.allowed_phrases || []).length})
                          </div>
                          <div className="space-y-1.5">
                            {(story.allowed_phrases || []).map((p, i) => (
                              <div key={p + i} className="flex items-center justify-between gap-2 px-3 py-2 border border-stone-800/70 bg-stone-900/20">
                                <span data-testid={`allowed-phrase-${i}`} className="font-serif-reading text-stone-400 text-xs italic truncate">&ldquo;{p}&rdquo;</span>
                                <button
                                  data-testid={`allowed-remove-${i}`}
                                  onClick={() => unallowPhrase(i)}
                                  className="text-stone-600 hover:text-rose-500 transition shrink-0"
                                  title="Stop whitelisting (auto-suppression resumes if it repeats)"
                                >
                                  <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </section>

                    {(story?.mode === "chat" || story?.chat_player_id) && (
                      <>
                        <div className="hairline"></div>
                        <section>
                          <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3">
                            Chat reply pace
                          </div>
                          <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                            How many messages the AI sends each turn in 1-on-1 chats. Group chats use their own group-dynamic rules.
                          </p>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              ["short", "Short", "1 reply"],
                              ["natural", "Natural", "1–3 replies"],
                              ["conversational", "Conversational", "3–5 replies"],
                            ].map(([val, label, hint]) => {
                              const active = (story?.chat_reply_count || "natural") === val;
                              return (
                                <button
                                  key={val}
                                  type="button"
                                  data-testid={`reply-count-${val}`}
                                  disabled={savingReplyCount}
                                  onClick={() => setStoryReplyCount(val)}
                                  className={`p-3 border text-left transition ${active
                                    ? "border-rose-800 bg-rose-950/25 text-rose-100"
                                    : "border-stone-800 bg-stone-900/30 text-stone-400 hover:border-stone-600"}`}
                                >
                                  <div className="font-serif-reading text-sm">{label}</div>
                                  <div className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500 mt-1">{hint}</div>
                                </button>
                              );
                            })}
                          </div>
                        </section>
                      </>
                    )}

                    {story?.mode === "scene" && (
                      <>
                        <div className="hairline"></div>
                        <section>
                          <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3">
                            Scene beat length
                          </div>
                          <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                            How many screenplay lines the AI writes per beat. Override per-turn from the composer.
                          </p>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              ["short", "Short", "2–3 lines"],
                              ["medium", "Medium", "4–6 lines"],
                              ["long", "Long", "7–10 lines"],
                            ].map(([val, label, hint]) => {
                              const active = (story?.beat_length || "medium") === val;
                              return (
                                <button
                                  key={val}
                                  type="button"
                                  data-testid={`beat-length-${val}`}
                                  disabled={savingBeatLength}
                                  onClick={() => setStoryBeatLength(val)}
                                  className={`p-3 border text-left transition ${active
                                    ? "border-rose-800 bg-rose-950/25 text-rose-100"
                                    : "border-stone-800 bg-stone-900/30 text-stone-400 hover:border-stone-600"}`}
                                >
                                  <div className="font-serif-reading text-sm">{label}</div>
                                  <div className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500 mt-1">{hint}</div>
                                </button>
                              );
                            })}
                          </div>
                        </section>
                        <section>
                          <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3">
                            Scene tone intensity
                          </div>
                          <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                            How saturated the per-character bubble colors appear. Saved on this device.
                          </p>
                          <div className="grid grid-cols-3 gap-2" data-testid="scene-vibe-picker">
                            {[
                              ["muted", "Muted", "Quiet hues"],
                              ["standard", "Standard", "Balanced"],
                              ["vivid", "Vivid", "Punchy"],
                            ].map(([val, label, hint]) => {
                              const active = sceneVibe === val;
                              return (
                                <button
                                  key={val}
                                  type="button"
                                  data-testid={`scene-vibe-${val}`}
                                  onClick={() => setSceneVibe(val)}
                                  className={`p-3 border text-left transition ${active
                                    ? "border-rose-800 bg-rose-950/25 text-rose-100"
                                    : "border-stone-800 bg-stone-900/30 text-stone-400 hover:border-stone-600"}`}
                                >
                                  <div className="font-serif-reading text-sm">{label}</div>
                                  <div className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500 mt-1">{hint}</div>
                                </button>
                              );
                            })}
                          </div>
                        </section>
                      </>
                    )}

                    <div className="hairline"></div>

                    {/* Emotional-arc toggle */}
                    <section>
                      <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3">
                        Relationship dynamic
                      </div>
                      <button
                        type="button"
                        data-testid="reader-emotional-arc-toggle"
                        disabled={savingEmotional}
                        onClick={toggleEmotionalArc}
                        className={`w-full flex items-start gap-3 p-4 border transition text-left ${story?.no_emotional_arc
                          ? "border-rose-800 bg-rose-950/25"
                          : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                      >
                        <div className={`mt-0.5 w-4 h-4 border flex items-center justify-center shrink-0 ${story?.no_emotional_arc ? "bg-rose-900 border-rose-800" : "border-stone-600 bg-stone-950"}`}>
                          {story?.no_emotional_arc && <Check className="w-3 h-3 text-rose-50" strokeWidth={2.5} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-sans-ui text-[11px] uppercase tracking-[0.22em] text-stone-300">
                            Keep it physical &mdash; no emotional arc
                          </div>
                          <div className="font-serif-reading italic text-stone-500 text-xs mt-1 leading-relaxed">
                            {story?.no_emotional_arc
                              ? "The AI won't manufacture bonding. Lust, tension, play — not love."
                              : "Stops forced feelings. Good for pure hookups, rivalries, tense play."}
                          </div>
                        </div>
                      </button>
                    </section>

                    <div className="hairline"></div>

                    {/* Intensity tier — change mid-story to escalate/de-escalate heat */}
                    <section>
                      <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3">
                        Intensity
                      </div>
                      <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                        Change the heat level for upcoming chapters. Past chapters stay as written. Takes effect on the next reply.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="reader-intensity-grid">
                        {[
                          { v: "story", l: "Story Only", d: "No romance" },
                          { v: "sweet", l: "Sweet", d: "No heat" },
                          { v: "mild", l: "Suggestive", d: "Implied" },
                          { v: "steamy", l: "Steamy", d: "Charged" },
                          { v: "explicit", l: "Explicit", d: "Unflinching" },
                          { v: "kink", l: "Kink", d: "BDSM / power play" },
                        ].map((o) => {
                          const active = (story?.intensity || "steamy") === o.v;
                          return (
                            <button
                              key={o.v}
                              type="button"
                              data-testid={`reader-intensity-${o.v}`}
                              disabled={savingIntensity || active}
                              onClick={() => setStoryIntensity(o.v)}
                              className={`p-3 border text-center transition ${
                                active
                                  ? "border-rose-800 bg-rose-950/25"
                                  : "border-stone-800 bg-stone-900/30 hover:border-stone-600"
                              }`}
                            >
                              <div className="font-serif-heading text-base text-stone-100">{o.l}</div>
                              <div className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500 mt-1">{o.d}</div>
                            </button>
                          );
                        })}
                      </div>
                    </section>

                    <div className="hairline"></div>

                    {/* Canon section */}
                    <section>
                      <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800 mb-3">
                        Running continuity
                      </div>
                      <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed mb-4">
                        What the AI has noted after each chapter. Click any entry to fix a mistake it wrote.
                      </p>
                      <div className="space-y-3">
                        {chapters.map((ch) => (
                          <div key={ch.id} data-testid={`canon-${ch.index}`} className="border border-stone-800 bg-stone-900/30 p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                                {isChat ? "Scene" : "Chapter"} {toRoman(ch.index)}
                              </div>
                              {editingCanonId !== ch.id && (
                                <button
                                  data-testid={`canon-edit-${ch.index}`}
                                  onClick={() => { setEditingCanonId(ch.id); setCanonBuffer(ch.canon || ""); }}
                                  className="text-stone-600 hover:text-stone-200"
                                >
                                  <Pencil className="w-3 h-3" strokeWidth={1.5} />
                                </button>
                              )}
                            </div>
                            {editingCanonId === ch.id ? (
                              <div className="space-y-2">
                                <Textarea
                                  data-testid={`canon-textarea-${ch.index}`}
                                  value={canonBuffer}
                                  onChange={(e) => setCanonBuffer(e.target.value)}
                                  className="bg-stone-900 border-stone-800 rounded-none text-stone-200 font-serif-reading text-sm min-h-[90px]"
                                />
                                <div className="flex justify-end gap-2">
                                  <Button variant="ghost" onClick={() => setEditingCanonId(null)} className="rounded-none h-8 px-3 text-stone-400 font-sans-ui text-[10px] uppercase tracking-[0.2em]">
                                    Cancel
                                  </Button>
                                  <Button
                                    data-testid={`canon-save-${ch.index}`}
                                    onClick={() => saveCanon(ch.id)}
                                    className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-8 px-3 font-sans-ui text-[10px] uppercase tracking-[0.2em]"
                                  >
                                    Save
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <p className="font-serif-reading text-stone-300 text-sm leading-relaxed italic">
                                {ch.canon || <span className="text-stone-600">(no snapshot)</span>}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  </div>
                </SheetContent>
              </Sheet>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="download-menu-btn" className="inline-flex items-center gap-2 font-sans-ui text-xs uppercase tracking-[0.25em] text-stone-500 hover:text-stone-200 transition">
                    <Download className="w-4 h-4" strokeWidth={1.5} /> Download
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-stone-950 border-stone-800 rounded-none w-52">
                  <DropdownMenuItem data-testid="download-pdf-item" onClick={() => downloadStory("pdf")} className="font-sans-ui text-xs uppercase tracking-[0.15em]">
                    <BookMarked className="w-3.5 h-3.5 mr-2" /> PDF (.pdf)
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="download-md-item" onClick={() => downloadStory("md")} className="font-sans-ui text-xs uppercase tracking-[0.15em]">
                    <BookMarked className="w-3.5 h-3.5 mr-2" /> Markdown (.md)
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="download-txt-item" onClick={() => downloadStory("txt")} className="font-sans-ui text-xs uppercase tracking-[0.15em]">
                    <BookMarked className="w-3.5 h-3.5 mr-2" /> Plain text (.txt)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Face-to-Face Mode portrait panel — sticky beneath sticky bar when ON */}
      {f2fEligible && faceToFaceOn && (
        <div
          className="sticky top-[6.5rem] z-20 bg-stone-950/85 backdrop-blur-xl border-b border-stone-800/40"
          data-testid="f2f-panel"
        >
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0 overflow-hidden border border-stone-800 bg-stone-900">
              {/* Static portrait fallback (always behind videos) */}
              {f2fSpeakerPortrait && (
                <img
                  src={f2fSpeakerPortrait}
                  alt={f2fSpeaker?.name || "Portrait"}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="f2f-portrait-image"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              {/* Idle loop video — plays unless a lipsync video is active */}
              {idleVideoUrl && !activeLipsyncUrl && (
                <video
                  key={idleVideoUrl}
                  src={idleVideoUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="f2f-idle-video"
                />
              )}
              {/* Lipsync video — appears on top when speaking */}
              {activeLipsyncUrl && (
                <video
                  key={activeLipsyncUrl}
                  ref={f2fVideoRef}
                  src={activeLipsyncUrl}
                  autoPlay
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="f2f-lipsync-video"
                />
              )}
              {idleLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-stone-950/60" data-testid="f2f-idle-loading">
                  <Loader2 className="w-6 h-6 text-stone-300 animate-spin" strokeWidth={1.5} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-rose-800 mb-1">
                Face-to-Face
              </div>
              <div className="font-serif-heading text-xl sm:text-2xl text-stone-100 truncate" data-testid="f2f-speaker-name">
                {f2fSpeaker?.name || "Portrait"}
              </div>
              <div className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-0.5">
                {activeLipsyncUrl ? "Speaking…" : idleLoading ? "Warming up…" : idleVideoUrl ? "Listening" : "Static"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFaceToFaceOn(false)}
              data-testid="f2f-close-btn"
              title="Close Face-to-Face"
              className="text-stone-500 hover:text-stone-100 transition"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {/* Main content column */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 sm:pt-12">
        {/* Title block */}
        <div className="text-center mb-16 sm:mb-20">
          <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.4em] text-rose-800/80 mb-6">
            {theme?.name}
          </div>
          <h1 className="font-serif-heading text-4xl sm:text-5xl md:text-6xl font-light text-stone-50 leading-[1.05] mb-6">
            {story.title}
          </h1>
          <div className="font-serif-reading italic text-stone-500 text-lg">
            featuring {characters.map(c => c.name).join(" & ")}
          </div>
          <div className="divider-ornament mt-10 text-sm">&#10042;</div>
        </div>

        {/* Chapters */}
        {chapters.map((ch, idx) => {
          const isLast = idx === chapters.length - 1;
          const isEditing = editingId === ch.id;
          const progress = chapterProgress[ch.id] ?? 0;
          const epigraph = idx > 0 ? getEpigraph(chapters[idx - 1]) : null;
          return (
            <article
              key={ch.id}
              ref={(el) => { if (el) chapterRefs.current[ch.id] = el; }}
              data-testid={`chapter-${ch.index}`}
              data-rewrite-chapter={ch.id}
              className="relative mb-20 fade-up group/chapter scroll-mt-24 pl-4 sm:pl-6"
            >
              {/* Reading progress bar */}
              <div className="absolute left-0 top-0 bottom-0 w-px bg-stone-800/60" aria-hidden="true" />
              <div
                className={`absolute left-0 top-0 w-px bg-rose-800/80 transition-[height] duration-150 ease-out ${progress >= 0.98 ? "progress-complete" : ""}`}
                style={{ height: `${progress * 100}%` }}
                aria-hidden="true"
                data-testid={`chapter-progress-${ch.index}`}
              />

              <div className="flex items-center gap-4 mb-10 sm:justify-center sm:relative">
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.4em] text-stone-600">
                  {chapterMode(ch) === "chat" ? "Scene" : "Chapter"} {toRoman(ch.index)}
                  {chapterMode(ch) === "chat" && ch.is_open && <span className="ml-3 text-rose-400 normal-case tracking-wide italic">· open</span>}
                  {ch.edited_at && !isEditing && <span className="ml-3 text-rose-800/80 normal-case tracking-wide italic">· edited</span>}
                </div>
                {!isEditing && (
                  <div className="ml-auto sm:ml-0 sm:absolute sm:right-0 flex items-center gap-2">
                    {chapterMode(ch) === "chat" && (
                      <button
                        data-testid={`chapter-cast-toggle-${ch.index}`}
                        onClick={() => toggleCastView(ch.id)}
                        title="Show cast state"
                        className={`p-2 border transition ${castState[ch.id]?.open
                          ? "text-rose-200 bg-rose-950/30 border-rose-900"
                          : "text-stone-500 hover:text-rose-300 border-stone-800 hover:border-rose-900"}`}
                      >
                        <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    )}
                    <button
                      data-testid={`chapter-edit-${ch.index}`}
                      onClick={() => startEdit(ch)}
                      title="Edit this chapter"
                      className="p-2 text-stone-500 hover:text-rose-300 border border-stone-800 hover:border-rose-900 transition"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                    {isLast && !generating && !regenerating && (
                      <button
                        data-testid={`chapter-regenerate-${ch.index}`}
                        onClick={regenerateLast}
                        title="Regenerate this chapter (same direction)"
                        className="p-2 text-stone-500 hover:text-rose-300 border border-stone-800 hover:border-rose-900 transition"
                      >
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`chapter-rewind-${ch.index}`}
                          title="Rewind — delete this chapter and everything after"
                          className="p-2 text-stone-500 hover:text-rose-300 border border-stone-800 hover:border-rose-900 transition"
                        >
                          <Rewind className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-stone-950 border-stone-800 rounded-none">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-serif-heading text-2xl text-stone-100">
                            Rewind to Chapter {toRoman(ch.index)}?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="font-serif-reading text-stone-400">
                            {isLast
                              ? <>This chapter will be deleted. You&rsquo;ll return to the previous chapter&rsquo;s choices.</>
                              : <>Chapter {toRoman(ch.index)} and every chapter after it will be permanently deleted. This cannot be undone.</>
                            }
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none bg-stone-900 border-stone-800 text-stone-300 font-sans-ui text-xs uppercase tracking-[0.2em]">
                            Keep
                          </AlertDialogCancel>
                          <AlertDialogAction
                            data-testid={`chapter-rewind-confirm-${ch.index}`}
                            onClick={() => rewindTo(ch)}
                            className="rounded-none bg-rose-900 hover:bg-rose-800 text-rose-50 font-sans-ui text-xs uppercase tracking-[0.2em]"
                          >
                            Rewind
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-4" data-testid={`chapter-edit-area-${ch.index}`}>
                  <Textarea
                    value={editBuffer}
                    onChange={(e) => setEditBuffer(e.target.value)}
                    data-testid={`chapter-edit-textarea-${ch.index}`}
                    className="bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading text-lg leading-loose focus:ring-rose-900 min-h-[400px]"
                    placeholder="Write this chapter in your own hand. Separate paragraphs with blank lines."
                  />
                  <div className="flex items-center justify-between">
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-600">
                      {editBuffer.length} characters
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" data-testid={`chapter-cancel-${ch.index}`} onClick={cancelEdit} disabled={savingEdit}
                        className="rounded-none h-10 px-4 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-xs uppercase tracking-[0.25em]">
                        <X className="w-4 h-4 mr-2" strokeWidth={1.5} /> Cancel
                      </Button>
                      <Button data-testid={`chapter-save-${ch.index}`} onClick={() => saveEdit(ch.id)} disabled={savingEdit}
                        className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-10 px-5 font-sans-ui text-xs uppercase tracking-[0.25em]">
                        <Check className="w-4 h-4 mr-2" strokeWidth={1.5} /> {savingEdit ? "Saving…" : "Save"}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {epigraph && (
                    <div data-testid={`chapter-epigraph-${ch.index}`} className="mb-8 pl-4 border-l border-rose-900/50">
                      <div className="font-sans-ui text-[9px] uppercase tracking-[0.3em] text-rose-800/80 mb-1.5">
                        {epigraph.label}
                      </div>
                      {epigraph.text && (
                        <div className="font-serif-reading italic text-stone-400 text-base leading-relaxed">
                          &ldquo;{epigraph.text}&rdquo;
                        </div>
                      )}
                    </div>
                  )}
                  <div className={chapterMode(ch) === "chat" || chapterMode(ch) === "scene" ? "" : "story-prose font-serif-reading text-stone-300 text-lg md:text-xl leading-loose"}>
                    {chapterMode(ch) === "chat" && castState[ch.id]?.open && (
                      <div data-testid={`cast-view-${ch.index}`} className="mb-6 p-4 border border-stone-800/80 bg-stone-900/30">
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-rose-800">
                            Cast — this moment
                          </div>
                          <button
                            data-testid={`cast-refresh-${ch.index}`}
                            onClick={() => loadCastState(ch.id, { refresh: true })}
                            disabled={castState[ch.id]?.loading}
                            className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-500 hover:text-rose-300 transition flex items-center gap-1.5"
                            title="Regenerate from current scene"
                          >
                            {castState[ch.id]?.loading ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> : <RefreshCw className="w-3 h-3" strokeWidth={1.5} />}
                            Refresh
                          </button>
                        </div>
                        {castState[ch.id]?.loading && !castState[ch.id]?.cast && (
                          <div className="font-serif-reading italic text-stone-500 text-sm">Reading the room&hellip;</div>
                        )}
                        {castState[ch.id]?.cast && (
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {castState[ch.id].cast.map((entry, ci) => {
                              const char = (characters || []).find(cc => cc.id === entry.character_id);
                              if (!char) return null;
                              const isPlayer = char.id === story.chat_player_id;
                              return (
                                <div key={entry.character_id} data-testid={`cast-card-${ci}`} className={`p-3 border ${isPlayer ? "border-rose-900/60 bg-rose-950/15" : "border-stone-800 bg-stone-950/40"}`}>
                                  <div className="flex items-start gap-3">
                                    {char.portrait_url ? (
                                      <img src={char.portrait_url} alt={char.name} className="w-10 h-10 rounded-full object-cover border border-stone-800 shrink-0" />
                                    ) : (
                                      <div className={`w-10 h-10 rounded-full border ${isPlayer ? "border-rose-900 bg-rose-950/40" : "border-stone-700 bg-stone-900"} flex items-center justify-center shrink-0`}>
                                        <UserCircle2 className="w-5 h-5 text-stone-500" strokeWidth={1.2} />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="font-serif-reading text-stone-100 text-sm leading-tight">
                                        {char.name}
                                        {isPlayer && <span className="ml-1.5 font-sans-ui text-[8px] uppercase tracking-[0.25em] text-rose-400">· you</span>}
                                      </div>
                                      {entry.location && (
                                        <div className="font-serif-reading italic text-stone-400 text-xs leading-snug mt-1">
                                          {entry.location}
                                        </div>
                                      )}
                                      {entry.mood && (
                                        <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-500 mt-1.5">
                                          {entry.mood}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {entry.relationships && Object.keys(entry.relationships).length > 0 && (
                                    <div className="mt-2.5 pt-2.5 border-t border-stone-800/60 space-y-1">
                                      {Object.entries(entry.relationships).map(([name, vibe]) => (
                                        <div key={name} className="flex items-baseline justify-between gap-2">
                                          <span className="font-sans-ui text-[9px] uppercase tracking-[0.22em] text-stone-600 truncate">w/ {name}</span>
                                          <span className="font-serif-reading italic text-rose-200/80 text-xs text-right truncate">{vibe || "—"}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {renderChapterBody(ch, idx === chapters.length - 1)}
                  </div>
                </>
              )}

              {idx < chapters.length - 1 && !isEditing && (
                chapterMode(ch) === "chat" ? (
                  <div className="mt-14 mb-2" data-testid={`scene-break-${ch.index}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedSceneCanon(expandedSceneCanon === ch.id ? null : ch.id)}
                      className="w-full group flex items-center gap-3 hover:opacity-100 opacity-70 transition"
                      title={ch.canon ? "Show scene summary" : "Scene break"}
                    >
                      <div className="flex-1 h-px bg-stone-800/60 group-hover:bg-rose-900/50 transition" aria-hidden="true" />
                      <div className="font-sans-ui text-[9px] uppercase tracking-[0.3em] text-stone-600 group-hover:text-rose-300 transition">
                        Scene {toRoman(ch.index)} · close
                      </div>
                      <div className="flex-1 h-px bg-stone-800/60 group-hover:bg-rose-900/50 transition" aria-hidden="true" />
                    </button>
                    {ch.canon && expandedSceneCanon === ch.id && (
                      <div data-testid={`scene-canon-${ch.index}`} className="mt-4 mx-auto max-w-md border border-stone-800 bg-stone-900/40 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-rose-800">
                            Snapshot
                          </div>
                          {editingCanonId !== ch.id && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                data-testid={`scene-canon-regen-${ch.index}`}
                                disabled={regeneratingCanonId === ch.id}
                                onClick={() => regenerateCanon(ch.id)}
                                className="text-stone-600 hover:text-rose-300 transition disabled:opacity-40"
                                title="Re-summarize this snapshot from the scene"
                              >
                                {regeneratingCanonId === ch.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                  : <RefreshCw className="w-3 h-3" strokeWidth={1.5} />
                                }
                              </button>
                              <button
                                type="button"
                                data-testid={`scene-canon-edit-${ch.index}`}
                                onClick={() => { setEditingCanonId(ch.id); setCanonBuffer(ch.canon || ""); }}
                                className="text-stone-600 hover:text-rose-300 transition"
                                title="Edit this snapshot"
                              >
                                <Pencil className="w-3 h-3" strokeWidth={1.5} />
                              </button>
                            </div>
                          )}
                        </div>
                        {editingCanonId === ch.id ? (
                          <div className="space-y-2">
                            <Textarea
                              data-testid={`scene-canon-textarea-${ch.index}`}
                              value={canonBuffer}
                              onChange={(e) => setCanonBuffer(e.target.value)}
                              className="bg-stone-900 border-stone-800 rounded-none text-stone-200 font-serif-reading text-sm leading-relaxed min-h-[120px]"
                            />
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                onClick={() => setEditingCanonId(null)}
                                className="rounded-none h-8 px-3 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[10px] uppercase tracking-[0.2em]"
                              >
                                Cancel
                              </Button>
                              <Button
                                data-testid={`scene-canon-save-${ch.index}`}
                                onClick={() => saveCanon(ch.id)}
                                className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-8 px-3 font-sans-ui text-[10px] uppercase tracking-[0.2em]"
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="font-serif-reading text-stone-400 italic text-sm leading-relaxed whitespace-pre-line">
                            {ch.canon}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="divider-ornament mt-16 text-sm">&#10042;&nbsp;&nbsp;&nbsp;&#10042;&nbsp;&nbsp;&nbsp;&#10042;</div>
                )
              )}
            </article>
          );
        })}

        {/* Streaming / continue zone */}
        <div className="mt-24 pt-16 border-t border-stone-800/60" ref={streamingRef}>
          {(generating || regenerating) ? (
            <div className="py-6" data-testid={regenerating ? "regenerating-state" : "generating-state"}>
              {streamingIndex && composerEffectiveMode !== "chat" && composerEffectiveMode !== "scene" && (
                <div className="text-center mb-10">
                  <div className="font-sans-ui text-[10px] uppercase tracking-[0.4em] text-stone-600">
                    {streamMode === "end" ? "Closing · " : streamMode === "regenerate" ? "Rewriting · " : ""}Chapter {toRoman(streamingIndex)}
                  </div>
                </div>
              )}
              {composerEffectiveMode === "chat" ? (
                <div className="pl-4 sm:pl-6 relative">
                  <div className="absolute left-0 top-0 bottom-0 w-px bg-rose-800/30" aria-hidden="true" />
                  {formatChatContent(streamingText || "", { showCaret: !!streamingText, showTyping: !streamingText })}
                </div>
              ) : composerEffectiveMode === "scene" ? (
                streamingText ? (
                  <div className="pl-4 sm:pl-6 relative" data-testid="streaming-scene">
                    <div className="absolute left-0 top-0 bottom-0 w-px bg-rose-800/30" aria-hidden="true" />
                    {(() => {
                      // Scene-mode streaming: render as a stable plain-text preview instead of
                      // structured bubbles. Bubbles are re-parsed on every token and can shift
                      // as new speaker cues arrive (e.g. `CHRIS: yes. But` becomes `CHRIS: yes.
                      // Then MARCUS: no.` once punctuation lands), causing beats to appear and
                      // vanish mid-stream. Plain text is monotonic — text only grows — so nothing
                      // ever disappears. The properly-structured bubbles snap in on `done` when
                      // the persisted (cleaned) chapter loads.
                      const playerFirstUpper = (playerName || "").trim().split(" ")[0].toUpperCase();
                      const currentCastFirsts = new Set(
                        (characters || [])
                          .map(c => (c.name || "").trim().split(" ")[0].toUpperCase())
                          .filter(Boolean)
                      );
                      // CRITICAL: buffer the last INCOMPLETE line (no trailing newline) so we
                      // never render a partial speaker prefix that could later be classified as
                      // drop. Otherwise the user sees "Kate" render → then vanish once ":" arrives
                      // and the classifier flags it as a 2nd-player / former-cast / past-cap line.
                      const endsWithNewline = /[\r\n]$/.test(streamingText);
                      const rawLines = streamingText.split(/\r?\n/);
                      // If the stream doesn't end on a newline, the last element is a partial
                      // line still being typed — hold it back from classification until it lands.
                      let tailPartial = "";
                      if (!endsWithNewline && rawLines.length > 0) {
                        tailPartial = rawLines.pop();
                      }
                      const beatCap = (() => {
                        const bl = (turnBeatLength || story?.beat_length || "medium").toLowerCase();
                        if (bl === "short") return 3;
                        if (bl === "long") return 10;
                        return 6;
                      })();
                      const forbiddenFirsts = new Set((data?.forbidden_speakers || []).map(n => (n || "").trim().split(" ")[0].toUpperCase()));
                      let npcLines = 0;
                      let sawPlayerEcho = false;
                      let cutAtPlayer = false;
                      let aiBeatCount = 0;
                      const rendered = [];
                      const labelRe = /^\s*\*?\s*([A-Za-z][A-Za-z .'\-]{0,30}?)\s*:\s*/;
                      for (let i = 0; i < rawLines.length; i++) {
                        const line = rawLines[i];
                        const t = line.trim();
                        if (!t) { rendered.push(""); continue; }
                        const m = t.match(labelRe);
                        const isDialogueLike = !!m;
                        const isPureAction = t.startsWith("*") && t.endsWith("*") && t.length >= 3 && (t.match(/\*/g) || []).length === 2;
                        if (isDialogueLike) {
                          const spk = m[1].trim().toUpperCase();
                          const spkFirst = spk.split(/\s+/)[0];
                          if (spk === "NARRATION" || spk === "SCENE" || spk === "TIME") {
                            if (aiBeatCount >= beatCap) break;
                            rendered.push(line);
                            aiBeatCount++;
                            continue;
                          }
                          if (playerFirstUpper && spkFirst === playerFirstUpper) {
                            // Opening player line = the echo (kept). Anywhere later = AI
                            // ventriloquism — backend cuts the reply there, so stop rendering.
                            if (!sawPlayerEcho && !rendered.some(r => r.trim())) {
                              sawPlayerEcho = true;
                              rendered.push(line);
                              continue;
                            }
                            cutAtPlayer = true;
                            break;
                          }
                          if (forbiddenFirsts.has(spkFirst)) continue;
                          if (!currentCastFirsts.has(spkFirst)) {
                            // Walk-on: backend keeps the first 2 per turn.
                            npcLines += 1;
                            if (npcLines > (story?.walk_ons_enabled === false ? 0 : 2)) continue;
                          }
                          if (aiBeatCount >= beatCap) break;
                          rendered.push(line);
                          aiBeatCount++;
                          continue;
                        }
                        if (isPureAction) {
                          if (aiBeatCount >= beatCap) break;
                          rendered.push(line);
                          aiBeatCount++;
                          continue;
                        }
                        // Free-form / continuation line — attach visually (don't count toward cap).
                        rendered.push(line);
                      }
                      // Only APPEND the incomplete tail to the on-screen text if we can prove
                      // it's safe (its rendering won't be reversed once complete). Safe cases:
                      //   • Tail already contains a `:` AND its speaker classifies as KEEP.
                      //   • Tail is clearly free-form (doesn't start with an alpha char that
                      //     could resolve into a droppable speaker) — e.g. starts with `*`
                      //     or punctuation or whitespace-only content.
                      // Otherwise, hold it back. The tail lands atomically on the next newline.
                      let tailToShow = "";
                      if (tailPartial && !cutAtPlayer) {
                        const tt = tailPartial.trim();
                        const startsLikeSpeaker = /^\*?\s*[A-Za-z]/.test(tt);
                        const hasColon = tt.includes(":");
                        if (!startsLikeSpeaker) {
                          // Clearly not a labeled beat (starts with `*` action or punctuation) — safe
                          tailToShow = tailPartial;
                        } else if (hasColon) {
                          // We can classify — apply the same filter
                          const tm = tt.match(labelRe);
                          if (tm) {
                            const spk = tm[1].trim().toUpperCase();
                            const spkFirst = spk.split(/\s+/)[0];
                            if (spk === "NARRATION" || spk === "SCENE" || spk === "TIME") {
                              if (aiBeatCount < beatCap) tailToShow = tailPartial;
                            } else if (playerFirstUpper && spkFirst === playerFirstUpper) {
                              if (!sawPlayerEcho && !rendered.some(r => r.trim())) tailToShow = tailPartial;
                            } else if (currentCastFirsts.has(spkFirst) || (!forbiddenFirsts.has(spkFirst) && npcLines < (story?.walk_ons_enabled === false ? 0 : 2))) {
                              if (aiBeatCount < beatCap) tailToShow = tailPartial;
                            }
                            // else: former-cast / extra walk-on → hold back until newline lands
                          }
                        }
                        // else: alpha-starting, no colon yet → hold back
                      }
                      const output = tailToShow
                        ? rendered.concat([tailToShow]).join("\n")
                        : rendered.join("\n");
                      return (
                        <div className="font-serif-reading text-stone-300 text-base sm:text-lg leading-relaxed whitespace-pre-wrap opacity-90">
                          {output}
                          <span className="inline-block w-2 h-5 bg-rose-800 ml-1 align-baseline animate-pulse"></span>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center">
                    <Loader2 className="w-5 h-5 text-rose-700 animate-spin mx-auto mb-6" strokeWidth={1.5} />
                    <p className="font-serif-reading italic text-stone-400 text-lg">
                      The next beats are forming…
                    </p>
                  </div>
                )
              ) : streamingText ? (
                <div className="story-prose font-serif-reading text-stone-300 text-lg md:text-xl leading-loose pl-4 sm:pl-6 relative">
                  <div className="absolute left-0 top-0 bottom-0 w-px bg-rose-800/30" aria-hidden="true" />
                  {(() => {
                    const paragraphs = streamingText.split(/\n\n+/).filter(Boolean);
                    return paragraphs.map((p, i) => {
                      const isLastP = i === paragraphs.length - 1;
                      return (
                        <p key={i} className={isLastP ? "" : "paragraph-fade"}>
                          {p.trim()}
                          {isLastP && (
                            <span className="inline-block w-2 h-5 bg-rose-800 ml-1 align-baseline animate-pulse"></span>
                          )}
                        </p>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className="text-center">
                  <Loader2 className="w-5 h-5 text-rose-700 animate-spin mx-auto mb-6" strokeWidth={1.5} />
                  <p className="font-serif-reading italic text-stone-400 text-lg">
                    {streamMode === "end" ? "The story is closing…" : regenerating ? "A new draft is forming…" : chapters.length === 0 ? "The opening is forming…" : "The next chapter is forming…"}
                  </p>
                </div>
              )}
            </div>
          ) : chapters.length > 0 ? (
            story.ended ? (
              <div className="text-center py-16" data-testid="story-ended-block">
                <div className="font-serif-heading italic text-4xl md:text-5xl text-stone-200 mb-6">Fin.</div>
                <div className="divider-ornament text-sm mb-8">&#10042;&nbsp;&nbsp;&nbsp;&#10042;&nbsp;&nbsp;&nbsp;&#10042;</div>
                <p className="font-serif-reading italic text-stone-500 text-base">
                  This story has come to its close.
                </p>
                <Link to="/create" className="inline-block mt-10" data-testid="start-new-story-link">
                  <Button className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-11 px-6 font-sans-ui text-xs uppercase tracking-[0.25em]">
                    Begin another story
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-10">
                  <Feather className="w-4 h-4 text-rose-800 mx-auto mb-4" strokeWidth={1.5} />
                  <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                    {composerEffectiveMode === "chat" ? "Your reply" : "Where does the story turn?"}
                  </div>
                </div>

                {canShowFormatToggle && (
                  <div data-testid="format-toggle" className="flex items-center justify-center mb-8">
                    <div className="inline-flex border border-stone-800 bg-stone-900/40">
                      {canMix && (
                      <button
                        type="button"
                        data-testid="format-toggle-chat"
                        onClick={() => {
                          if (needsPlayerPick) {
                            // Open dialog to pick which character the user plays as.
                            setPlayerPickerTarget("chat");
                            setPlayerPickerChoice(story?.character_ids?.[0] || "");
                            setPlayerPickerOpen(true);
                            return;
                          }
                          setComposerMode("chat");
                        }}
                        className={`flex items-center gap-2 px-4 py-2 font-sans-ui text-[10px] uppercase tracking-[0.25em] transition ${
                          composerEffectiveMode === "chat"
                            ? "bg-rose-900/40 text-rose-200"
                            : "text-stone-500 hover:text-stone-200"
                        }`}
                      >
                        <MessageCircle className="w-3 h-3" strokeWidth={1.5} /> Texts
                      </button>
                      )}
                      <button
                        type="button"
                        data-testid="format-toggle-prose"
                        onClick={() => {
                          if (currentSceneOpen) {
                            if (!confirm(`Switching to prose will close the current ${chapterMode(lastChapter) === "scene" ? "scene" : "texting thread"} first (a summary will be saved). Continue?`)) return;
                          }
                          setComposerMode("prose");
                        }}
                        className={`flex items-center gap-2 px-4 py-2 font-sans-ui text-[10px] uppercase tracking-[0.25em] border-l border-stone-800 transition ${
                          composerEffectiveMode === "prose"
                            ? "bg-rose-900/40 text-rose-200"
                            : "text-stone-500 hover:text-stone-200"
                        }`}
                      >
                        <BookOpen className="w-3 h-3" strokeWidth={1.5} /> Prose
                      </button>
                      {canScene && (
                      <button
                        type="button"
                        data-testid="format-toggle-scene"
                        onClick={() => {
                          if (needsPlayerPick) {
                            // Open dialog to pick which character the user plays in Scene mode.
                            setPlayerPickerTarget("scene");
                            setPlayerPickerChoice(story?.character_ids?.[0] || "");
                            setPlayerPickerOpen(true);
                            return;
                          }
                          setComposerMode("scene");
                        }}
                        className={`flex items-center gap-2 px-4 py-2 font-sans-ui text-[10px] uppercase tracking-[0.25em] border-l border-stone-800 transition ${
                          composerEffectiveMode === "scene"
                            ? "bg-rose-900/40 text-rose-200"
                            : "text-stone-500 hover:text-stone-200"
                        }`}
                      >
                        <PenLine className="w-3 h-3" strokeWidth={1.5} /> Scene
                      </button>
                      )}
                    </div>
                  </div>
                )}

                {composerEffectiveMode === "chat" ? (
                  <div className="space-y-4" data-testid="chat-composer">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-rose-300">
                        Texting as {playerName || "you"}
                      </div>
                      <div className="flex items-center gap-3">
                        {currentSceneOpen && (
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600">
                            {playerTurnsInScene} {playerTurnsInScene === 1 ? "reply" : "replies"} in this scene
                          </div>
                        )}
                        {/* Reply mode toggle (group chats only — pointless with 1 AI char) */}
                        {aiChars.length >= 2 && (
                          <div data-testid="reply-mode-toggle" className="inline-flex border border-stone-800 bg-stone-900/40 text-[9px]">
                            <button
                              type="button"
                              data-testid="reply-mode-set-auto"
                              disabled={savingReplyMode}
                              onClick={() => { setDirectedQueue([]); if (replyMode !== "auto") setStoryReplyMode("auto"); }}
                              className={`px-2 py-1 font-sans-ui uppercase tracking-[0.2em] transition ${
                                replyMode === "auto" ? "bg-rose-900/40 text-rose-200" : "text-stone-500 hover:text-stone-200"
                              }`}
                            >
                              Natural
                            </button>
                            <button
                              type="button"
                              data-testid="reply-mode-set-directed"
                              disabled={savingReplyMode}
                              onClick={() => { if (replyMode !== "directed") setStoryReplyMode("directed"); }}
                              className={`px-2 py-1 font-sans-ui uppercase tracking-[0.2em] border-l border-stone-800 transition ${
                                replyMode === "directed" ? "bg-rose-900/40 text-rose-200" : "text-stone-500 hover:text-stone-200"
                              }`}
                            >
                              Directed
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {sceneNudge && (
                      <div data-testid="scene-nudge" className="border border-rose-900/40 bg-rose-950/15 p-3 flex items-start gap-3">
                        <Sparkles className="w-3.5 h-3.5 text-rose-700 mt-0.5 shrink-0" strokeWidth={1.5} />
                        <div className="font-serif-reading italic text-stone-400 text-xs leading-relaxed flex-1">
                          This scene is getting long. Consider closing it — a summary will be saved and your next message will start a new scene.
                        </div>
                      </div>
                    )}
                    {/* Cast presence — who is currently "in" this scene */}
                    {currentSceneOpen && aiChars.length >= 2 && (
                      <div data-testid="cast-presence-wrap-chat" className="border border-stone-800/60 bg-stone-900/30 rounded-2xl p-3 space-y-2">
                        <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500 flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2">
                            <Users className="w-3 h-3" strokeWidth={1.5} />
                            Cast in this scene
                          </span>
                          {activeAiChars.length >= 2 && (
                            <span className="text-stone-600 normal-case tracking-normal text-[10px]">
                              drag to set reply order
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {orderedPanelChars.map(c => {
                            const isPresent = activeAiCharIds ? activeAiCharIds.has(c.id) : true;
                            const first = (c.name || "").split(" ")[0];
                            const isDragging = dragCharId === c.id;
                            const isDragTarget = dragOverCharId === c.id && dragCharId && dragCharId !== c.id;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                data-testid={`cast-toggle-${c.id}`}
                                disabled={generating || savingActiveCast}
                                draggable={isPresent && !pendingToggleCharId}
                                onDragStart={(e) => { if (!isPresent) return; setDragCharId(c.id); try { e.dataTransfer.effectAllowed = "move"; } catch (_) { /* ignore */ } }}
                                onDragOver={(e) => { if (isPresent && dragCharId && dragCharId !== c.id) { e.preventDefault(); setDragOverCharId(c.id); } }}
                                onDragLeave={() => { if (dragOverCharId === c.id) setDragOverCharId(null); }}
                                onDrop={(e) => { e.preventDefault(); const src = dragCharId; setDragCharId(null); setDragOverCharId(null); if (isPresent && src && src !== c.id) reorderActiveCast(src, c.id); }}
                                onDragEnd={() => { setDragCharId(null); setDragOverCharId(null); }}
                                onClick={() => openCastToggle(c.id, isPresent ? "leave" : "join")}
                                title={isPresent ? `${c.name} is in the scene — drag to reorder priority, click to step them out` : `${c.name} is off-stage — click to bring them in`}
                                className={`font-sans-ui text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition flex items-center gap-1.5 ${
                                  isPresent
                                    ? "border-rose-800 bg-rose-950/30 text-rose-200 hover:bg-rose-900/40 cursor-grab active:cursor-grabbing"
                                    : "border-stone-800 bg-stone-900/30 text-stone-500 hover:text-stone-200 hover:border-stone-600"
                                } ${isDragging ? "opacity-40" : ""} ${isDragTarget ? "ring-2 ring-rose-600 ring-offset-1 ring-offset-stone-950" : ""}`}
                              >
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${isPresent ? "bg-rose-400" : "bg-stone-600"}`}></span>
                                {first}
                              </button>
                            );
                          })}
                        </div>
                        {pendingToggleCharId && pendingToggleAction && (() => {
                          const c = (characters || []).find(x => x.id === pendingToggleCharId);
                          if (!c) return null;
                          const first = (c.name || "").split(" ")[0];
                          return (
                            <div data-testid="cast-toggle-narration-editor" className="border-t border-stone-800/60 pt-3 space-y-2">
                              <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-rose-700">
                                {pendingToggleAction === "join" ? `Bring ${first} in` : `Step ${first} out`} — add a NARRATION line (or leave blank to do it silently)
                              </div>
                              <Input
                                data-testid="cast-toggle-narration-input"
                                value={pendingNarrationText}
                                onChange={(e) => setPendingNarrationText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); submitCastToggle(); }
                                  else if (e.key === "Escape") cancelCastToggle();
                                }}
                                placeholder="leave blank for a silent toggle"
                                autoFocus
                                className="bg-stone-900 border-stone-800 rounded-lg text-stone-100 font-serif-reading text-sm focus:ring-rose-900"
                              />
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  data-testid="cast-toggle-cancel-btn"
                                  onClick={cancelCastToggle}
                                  disabled={savingActiveCast}
                                  className="rounded-full h-8 px-3 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[10px] uppercase tracking-[0.25em]"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  data-testid="cast-toggle-confirm-btn"
                                  onClick={submitCastToggle}
                                  disabled={savingActiveCast}
                                  className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-full h-8 px-4 font-sans-ui text-[10px] uppercase tracking-[0.25em]"
                                >
                                  {savingActiveCast ? "Saving…" : (pendingToggleAction === "join" ? "Bring them in" : "Step them out")}
                                </Button>
                              </div>
                            </div>
                          );
                        })()}
                        {offstageAiChars.length > 0 && !pendingToggleCharId && (
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600">
                            Off-stage: {offstageAiChars.map(c => (c.name || "").split(" ")[0]).join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Manage Cast — always shown in chat/scene composer, independent of
                        cast-presence panel which requires >=2 AI chars. */}
                    {renderManageCastPanel()}
                    <CharacterDirectionPanel storyId={id} aiChars={aiChars} direction={story?.character_direction || {}} onChanged={load} />
                    {/* Set Scene / Time — manually insert a NARRATION beat without invoking the AI */}
                    {currentSceneOpen && (
                      <div data-testid="inject-narration-wrap-chat" className="border border-stone-800/60 bg-stone-900/30 rounded-2xl">
                        {!injectNarrationOpen ? (
                          <button
                            type="button"
                            data-testid="inject-narration-open-btn-chat"
                            onClick={() => setInjectNarrationOpen(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-stone-500 hover:text-rose-300 font-sans-ui text-[10px] uppercase tracking-[0.25em] transition"
                          >
                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                            Set scene / time
                          </button>
                        ) : (
                          <div className="p-3 space-y-2">
                            <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500 flex items-center gap-2">
                              <Clock className="w-3 h-3" strokeWidth={1.5} />
                              Insert a NARRATION beat (time / location anchor)
                            </div>
                            <Input
                              data-testid="inject-narration-input"
                              value={injectNarrationText}
                              onChange={(e) => setInjectNarrationText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && injectNarrationText.trim()) {
                                  e.preventDefault();
                                  submitInjectNarration();
                                } else if (e.key === "Escape") {
                                  setInjectNarrationOpen(false);
                                  setInjectNarrationText("");
                                }
                              }}
                              placeholder='e.g. "An hour later — the bar" or "Tuesday morning, 7am"'
                              autoFocus
                              className="bg-stone-900 border-stone-800 rounded-lg text-stone-100 font-serif-reading text-sm focus:ring-rose-900"
                            />
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                data-testid="inject-narration-cancel-btn"
                                onClick={() => { setInjectNarrationOpen(false); setInjectNarrationText(""); }}
                                disabled={injectingNarration}
                                className="rounded-full h-8 px-3 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[10px] uppercase tracking-[0.25em]"
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                data-testid="inject-narration-submit-btn"
                                onClick={submitInjectNarration}
                                disabled={injectingNarration || !injectNarrationText.trim()}
                                className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-full h-8 px-4 font-sans-ui text-[10px] uppercase tracking-[0.25em]"
                              >
                                {injectingNarration ? "Inserting…" : "Insert beat"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <Textarea
                      data-testid="chat-message-input"
                      value={freeText}
                      onChange={(e) => setFreeText(e.target.value)}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && freeText.trim()) {
                          sendChatTurn();
                        }
                      }}
                      placeholder="Type a message…"
                      className="bg-stone-900 border-stone-800 rounded-2xl text-stone-100 font-serif-reading text-base leading-relaxed focus:ring-rose-900 min-h-[90px]"
                    />
                    {/* Directed-mode AI character chips: tap to queue. Empty box + queue = nudge. Has text = directed reply. */}
                    {replyMode === "directed" && aiChars.length >= 1 && (
                      <div data-testid="directed-chips" className="space-y-2">
                        <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500">
                          {directedQueue.length === 0 ? (
                            currentSceneOpen
                              ? "Tap names to queue who speaks · type a message OR leave empty to nudge"
                              : "Pick who replies (tap multiple to queue in order)"
                          ) : (
                            <>Queued: {directedQueue.map((qid, i) => {
                              const c = aiChars.find(a => a.id === qid);
                              const firstName = (c?.name || "?").split(" ")[0];
                              return <span key={i} className="text-rose-300 mr-1">{firstName}{i < directedQueue.length - 1 ? " →" : ""}</span>;
                            })}{" "}<button type="button" onClick={() => setDirectedQueue([])} className="text-stone-500 hover:text-rose-300 ml-2">clear</button></>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {activeAiChars.map(c => {
                            const firstName = (c.name || "").split(" ")[0];
                            return (
                              <button
                                key={c.id}
                                type="button"
                                disabled={generating}
                                data-testid={`directed-chip-${c.id}`}
                                onClick={() => setDirectedQueue(q => [...q, c.id])}
                                title={`Queue ${c.name}`}
                                className="font-sans-ui text-[10px] uppercase tracking-[0.25em] border border-stone-700 bg-stone-900 text-stone-300 hover:text-rose-200 hover:border-rose-800 px-3 py-1.5 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                              >
                                <UserCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                {firstName}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600">
                        {freeText.trim().length} chars · ⌘/Ctrl + Enter to send
                      </div>
                      <div className="flex items-center gap-2">
                        {currentSceneOpen && (
                          <button
                            type="button"
                            data-testid="end-scene-btn"
                            onClick={endScene}
                            className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500 hover:text-rose-300 border border-stone-800 hover:border-rose-800 px-3 py-2 transition"
                          >
                            End the scene
                          </button>
                        )}
                        {(() => {
                          const hasText = freeText.trim().length > 0;
                          // Nudge cases:
                          // - Directed mode (group chat): empty text + queued targets
                          // - 1-AI chat (auto mode): empty text + open scene → auto-nudge that single AI
                          const isDirectedNudge = !hasText && directedQueue.length > 0 && currentSceneOpen;
                          const isAutoNudge = !hasText && currentSceneOpen && aiChars.length === 1;
                          const isNudge = isDirectedNudge || isAutoNudge;
                          const disabled =
                            generating ||
                            (!hasText && !isNudge) ||
                            (hasText && replyMode === "directed" && aiChars.length >= 2 && directedQueue.length === 0);
                          const soloAiName = isAutoNudge ? (aiChars[0]?.name || "").split(" ")[0] : "";
                          return (
                            <Button
                              data-testid="submit-chat-message-btn"
                              disabled={disabled}
                              onClick={() => {
                                if (isAutoNudge) {
                                  // Fire a single-AI nudge: empty text + target the lone AI character
                                  doNudgeForCharIds([aiChars[0].id]);
                                } else {
                                  sendChatTurn();
                                }
                              }}
                              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-full h-11 px-6 font-sans-ui text-xs uppercase tracking-[0.25em]"
                            >
                              {isDirectedNudge
                                ? `Nudge ▸ ${directedQueue.length}`
                                : isAutoNudge
                                  ? `Nudge ${soloAiName || "AI"}`
                                  : "Send"}
                            </Button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ) : composerEffectiveMode === "scene" ? (
                  <div className="space-y-4" data-testid="scene-composer">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-rose-300">
                        Writing as {(playerName || "you").split(" ")[0]}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600 hidden sm:block">
                          Wrap a line in <span className="text-rose-300">*asterisks*</span> for an action beat
                        </div>
                        {/* Reply mode toggle (only useful with 2+ AI chars) */}
                        {aiChars.length >= 2 && (
                          <div data-testid="scene-reply-mode-toggle" className="inline-flex border border-stone-800 bg-stone-900/40 text-[9px]">
                            <button
                              type="button"
                              data-testid="scene-reply-mode-auto"
                              disabled={savingReplyMode}
                              onClick={() => { setDirectedQueue([]); if (replyMode !== "auto") setStoryReplyMode("auto"); }}
                              className={`px-2 py-1 font-sans-ui uppercase tracking-[0.2em] transition ${
                                replyMode === "auto" ? "bg-rose-900/40 text-rose-200" : "text-stone-500 hover:text-stone-200"
                              }`}
                            >
                              Natural
                            </button>
                            <button
                              type="button"
                              data-testid="scene-reply-mode-directed"
                              disabled={savingReplyMode}
                              onClick={() => { if (replyMode !== "directed") setStoryReplyMode("directed"); }}
                              className={`px-2 py-1 font-sans-ui uppercase tracking-[0.2em] border-l border-stone-800 transition ${
                                replyMode === "directed" ? "bg-rose-900/40 text-rose-200" : "text-stone-500 hover:text-stone-200"
                              }`}
                            >
                              Directed
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Set Scene / Time — manually insert a NARRATION beat without invoking the AI */}
                    {/* Cast presence — who is currently "in" this scene (scene/screenplay mode) */}
                    {lastChapter?.is_open === true && aiChars.length >= 2 && (
                      <div data-testid="cast-presence-wrap-scene" className="border border-stone-800/60 bg-stone-900/30 rounded-2xl p-3 space-y-2">
                        <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500 flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2">
                            <Users className="w-3 h-3" strokeWidth={1.5} />
                            Cast in this scene
                          </span>
                          {activeAiChars.length >= 2 && (
                            <span className="text-stone-600 normal-case tracking-normal text-[10px]">
                              drag to set reply order
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {orderedPanelChars.map(c => {
                            const isPresent = activeAiCharIds ? activeAiCharIds.has(c.id) : true;
                            const first = (c.name || "").split(" ")[0];
                            const isDragging = dragCharId === c.id;
                            const isDragTarget = dragOverCharId === c.id && dragCharId && dragCharId !== c.id;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                data-testid={`cast-toggle-scene-${c.id}`}
                                disabled={generating || savingActiveCast}
                                draggable={isPresent && !pendingToggleCharId}
                                onDragStart={(e) => { if (!isPresent) return; setDragCharId(c.id); try { e.dataTransfer.effectAllowed = "move"; } catch (_) { /* ignore */ } }}
                                onDragOver={(e) => { if (isPresent && dragCharId && dragCharId !== c.id) { e.preventDefault(); setDragOverCharId(c.id); } }}
                                onDragLeave={() => { if (dragOverCharId === c.id) setDragOverCharId(null); }}
                                onDrop={(e) => { e.preventDefault(); const src = dragCharId; setDragCharId(null); setDragOverCharId(null); if (isPresent && src && src !== c.id) reorderActiveCast(src, c.id); }}
                                onDragEnd={() => { setDragCharId(null); setDragOverCharId(null); }}
                                onClick={() => openCastToggle(c.id, isPresent ? "leave" : "join")}
                                title={isPresent ? `${c.name} is in the scene — drag to reorder priority, click to step them out` : `${c.name} is off-stage — click to bring them in`}
                                className={`font-sans-ui text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition flex items-center gap-1.5 ${
                                  isPresent
                                    ? "border-rose-800 bg-rose-950/30 text-rose-200 hover:bg-rose-900/40 cursor-grab active:cursor-grabbing"
                                    : "border-stone-800 bg-stone-900/30 text-stone-500 hover:text-stone-200 hover:border-stone-600"
                                } ${isDragging ? "opacity-40" : ""} ${isDragTarget ? "ring-2 ring-rose-600 ring-offset-1 ring-offset-stone-950" : ""}`}
                              >
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${isPresent ? "bg-rose-400" : "bg-stone-600"}`}></span>
                                {first}
                              </button>
                            );
                          })}
                        </div>
                        {pendingToggleCharId && pendingToggleAction && (() => {
                          const c = (characters || []).find(x => x.id === pendingToggleCharId);
                          if (!c) return null;
                          const first = (c.name || "").split(" ")[0];
                          return (
                            <div data-testid="cast-toggle-narration-editor-scene" className="border-t border-stone-800/60 pt-3 space-y-2">
                              <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-rose-700">
                                {pendingToggleAction === "join" ? `Bring ${first} in` : `Step ${first} out`} — add a NARRATION line (or leave blank to do it silently)
                              </div>
                              <Input
                                data-testid="cast-toggle-narration-input-scene"
                                value={pendingNarrationText}
                                onChange={(e) => setPendingNarrationText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); submitCastToggle(); }
                                  else if (e.key === "Escape") cancelCastToggle();
                                }}
                                placeholder="leave blank for a silent toggle"
                                autoFocus
                                className="bg-stone-900 border-stone-800 rounded-lg text-stone-100 font-serif-reading text-sm focus:ring-rose-900"
                              />
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  data-testid="cast-toggle-cancel-btn-scene"
                                  onClick={cancelCastToggle}
                                  disabled={savingActiveCast}
                                  className="rounded-full h-8 px-3 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[10px] uppercase tracking-[0.25em]"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  data-testid="cast-toggle-confirm-btn-scene"
                                  onClick={submitCastToggle}
                                  disabled={savingActiveCast}
                                  className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-full h-8 px-4 font-sans-ui text-[10px] uppercase tracking-[0.25em]"
                                >
                                  {savingActiveCast ? "Saving…" : (pendingToggleAction === "join" ? "Bring them in" : "Step them out")}
                                </Button>
                              </div>
                            </div>
                          );
                        })()}
                        {offstageAiChars.length > 0 && !pendingToggleCharId && (
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600">
                            Off-stage: {offstageAiChars.map(c => (c.name || "").split(" ")[0]).join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Manage Cast — always shown in chat/scene composer, independent of
                        cast-presence panel which requires >=2 AI chars. */}
                    {renderManageCastPanel()}
                    <CharacterDirectionPanel storyId={id} aiChars={aiChars} direction={story?.character_direction || {}} onChanged={load} />
                    {/* Set Scene / Time — manually insert a NARRATION beat without invoking the AI */}
                    {lastChapter?.is_open === true && (
                      <WalkOnsPanel storyId={id} npcs={story?.npcs || []} enabled={story?.walk_ons_enabled !== false} onChanged={load} />
                    )}
                    {lastChapter?.is_open === true && (
                      <StandingNotesPanel storyId={id} notes={story?.standing_notes || []} onChanged={load} />
                    )}
                    {lastChapter?.is_open === true && (
                      <div data-testid="inject-narration-wrap-scene" className="border border-stone-800/60 bg-stone-900/30 rounded-2xl">
                        {!injectNarrationOpen ? (
                          <button
                            type="button"
                            data-testid="inject-narration-open-btn-scene"
                            onClick={() => setInjectNarrationOpen(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-stone-500 hover:text-rose-300 font-sans-ui text-[10px] uppercase tracking-[0.25em] transition"
                          >
                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                            Set scene / time
                          </button>
                        ) : (
                          <div className="p-3 space-y-2">
                            <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500 flex items-center gap-2">
                              <Clock className="w-3 h-3" strokeWidth={1.5} />
                              Insert a NARRATION beat (time / location anchor)
                            </div>
                            <Input
                              data-testid="inject-narration-input-scene"
                              value={injectNarrationText}
                              onChange={(e) => setInjectNarrationText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && injectNarrationText.trim()) {
                                  e.preventDefault();
                                  submitInjectNarration();
                                } else if (e.key === "Escape") {
                                  setInjectNarrationOpen(false);
                                  setInjectNarrationText("");
                                }
                              }}
                              placeholder='e.g. "An hour later — the bar" or "Three days pass"'
                              autoFocus
                              className="bg-stone-900 border-stone-800 rounded-lg text-stone-100 font-serif-reading text-sm focus:ring-rose-900"
                            />
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                data-testid="inject-narration-cancel-btn-scene"
                                onClick={() => { setInjectNarrationOpen(false); setInjectNarrationText(""); }}
                                disabled={injectingNarration}
                                className="rounded-full h-8 px-3 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[10px] uppercase tracking-[0.25em]"
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                data-testid="inject-narration-submit-btn-scene"
                                onClick={submitInjectNarration}
                                disabled={injectingNarration || !injectNarrationText.trim()}
                                className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-full h-8 px-4 font-sans-ui text-[10px] uppercase tracking-[0.25em]"
                              >
                                {injectingNarration ? "Inserting…" : "Insert beat"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <Textarea
                      data-testid="scene-message-input"
                      value={freeText}
                      onChange={(e) => setFreeText(e.target.value)}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && freeText.trim()) {
                          continueStory({ free_text: freeText });
                        }
                      }}
                      placeholder={`${(playerName || "you").split(" ")[0]}'s next line — or *an action*`}
                      className="bg-stone-900 border-stone-800 rounded-2xl text-stone-100 font-sans-ui text-base leading-relaxed focus:ring-rose-900 min-h-[90px]"
                    />
                    {/* Directed-mode AI character chips: tap to queue. Empty box + queue = nudge. Has text = directed reply. */}
                    {replyMode === "directed" && aiChars.length >= 1 && (
                      <div data-testid="scene-directed-chips" className="space-y-2">
                        <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500">
                          {directedQueue.length === 0 ? (
                            "Tap names to queue who speaks · type a line OR leave empty to nudge"
                          ) : (
                            <>Queued: {directedQueue.map((qid, i) => {
                              const c = aiChars.find(a => a.id === qid);
                              const firstName = (c?.name || "?").split(" ")[0];
                              return <span key={i} className="text-rose-300 mr-1">{firstName}{i < directedQueue.length - 1 ? " →" : ""}</span>;
                            })}{" "}<button type="button" onClick={() => setDirectedQueue([])} className="text-stone-500 hover:text-rose-300 ml-2">clear</button></>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {activeAiChars.map(c => {
                            const firstName = (c.name || "").split(" ")[0];
                            return (
                              <button
                                key={c.id}
                                type="button"
                                disabled={generating}
                                data-testid={`scene-directed-chip-${c.id}`}
                                onClick={() => setDirectedQueue(q => [...q, c.id])}
                                title={`Queue ${c.name}`}
                                className="font-sans-ui text-[10px] uppercase tracking-[0.25em] border border-stone-700 bg-stone-900 text-stone-300 hover:text-rose-200 hover:border-rose-800 px-3 py-1.5 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                              >
                                <UserCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                {firstName}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {freeText.trim() && (
                      <div className="border border-stone-800/60 bg-stone-900/20 p-4" data-testid="scene-preview">
                        <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600 mb-2">Preview</div>
                        {(() => {
                          const txt = freeText.trim();
                          const playerFirst = (playerName || "you").split(" ")[0];
                          const playerFirstUpper = playerFirst.toUpperCase();
                          const tone = sceneToneMap[playerFirstUpper] || 8;
                          if (txt.startsWith("*") && txt.endsWith("*") && txt.length >= 3 && (txt.match(/\*/g) || []).length === 2) {
                            return (
                              <div className="text-center py-1">
                                <span className="inline-block font-sans-ui italic text-[13px] tracking-wide text-stone-500">
                                  *{txt.slice(1, -1)}*
                                </span>
                              </div>
                            );
                          }
                          // Split into segments like parseSceneContent does
                          const segments = [];
                          const re = /\*([^*]+)\*/g;
                          let cursor = 0;
                          let mm;
                          while ((mm = re.exec(txt)) !== null) {
                            const before = txt.slice(cursor, mm.index).trim();
                            if (before) segments.push({ type: "dialogue", text: before });
                            segments.push({ type: "action", text: mm[1].trim() });
                            cursor = mm.index + mm[0].length;
                          }
                          const tail = txt.slice(cursor).trim();
                          if (tail) segments.push({ type: "dialogue", text: tail });
                          if (segments.length === 0) segments.push({ type: "dialogue", text: txt });
                          return (
                            <div className="flex flex-col items-end text-right">
                              <div className={`font-sans-ui text-sm tracking-wide mb-1 scene-tone-${tone}-name`}>
                                ({playerFirst.charAt(0).toUpperCase() + playerFirst.slice(1).toLowerCase()})
                              </div>
                              <div className="pr-6 sm:pr-10 space-y-1.5">
                                {segments.map((s, j) => {
                                  if (s.type === "action") {
                                    return (
                                      <div key={`p-${j}`} className="font-sans-ui italic text-[13px] tracking-wide text-stone-500">
                                        *{s.text}*
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={`p-${j}`} className={`inline-block max-w-[88%] font-serif-reading text-lg leading-relaxed px-3.5 py-2 rounded-2xl rounded-tr-sm scene-tone-${tone}-bub scene-tone-${tone}-txt`}>
                                      {renderInlineItalics(s.text, `p-${j}`)}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2" data-testid="scene-beat-length-chip">
                        <span className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600">AI beat length:</span>
                        {[
                          ["short", "S"],
                          ["medium", "M"],
                          ["long", "L"],
                        ].map(([val, label]) => {
                          const active = (turnBeatLength || story?.beat_length || "medium") === val;
                          return (
                            <button
                              key={val}
                              type="button"
                              data-testid={`scene-beat-chip-${val}`}
                              onClick={() => setTurnBeatLength(val)}
                              className={`px-2 py-0.5 font-sans-ui text-[10px] uppercase tracking-[0.2em] border transition ${
                                active
                                  ? "border-rose-700 bg-rose-900/30 text-rose-200"
                                  : "border-stone-800 bg-stone-900 text-stone-500 hover:text-stone-200 hover:border-stone-600"
                              }`}
                              title={val === "short" ? "2–3 lines" : val === "long" ? "7–10 lines" : "4–6 lines"}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      {(() => {
                        const hasText = freeText.trim().length > 0;
                        const isDirectedNudge = !hasText && replyMode === "directed" && directedQueue.length > 0;
                        const canNaturalNudge = !hasText && replyMode !== "directed" && lastChapter?.is_open && lastChapter?.mode === "scene" && aiChars.length >= 1;
                        const sendDisabled = generating || (!hasText && !isDirectedNudge);
                        return (
                          <div className="flex items-center gap-2">
                            {currentSceneOpen && (
                              <button
                                type="button"
                                data-testid="end-scene-btn-scene"
                                onClick={endScene}
                                className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500 hover:text-rose-300 border border-stone-800 hover:border-rose-800 px-3 py-2 transition"
                                title="Close this scene — generates a canon summary, your next message starts a fresh scene with a clean break"
                              >
                                End scene
                              </button>
                            )}
                            {canNaturalNudge && (
                              <Button
                                type="button"
                                data-testid="scene-nudge-btn"
                                disabled={generating}
                                onClick={() => doNudgeForCharIds(aiChars.map(c => c.id))}
                                variant="outline"
                                className="border-stone-700 bg-stone-900 hover:bg-stone-800 text-stone-300 hover:text-rose-200 hover:border-rose-800 rounded-full h-11 px-4 font-sans-ui text-xs uppercase tracking-[0.25em]"
                                title="Skip your turn — let the AI keep the scene going"
                              >
                                Nudge
                              </Button>
                            )}
                            <Button
                              data-testid="submit-scene-message-btn"
                              disabled={sendDisabled}
                              onClick={() => continueStory({ free_text: freeText })}
                              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-full h-11 px-6 font-sans-ui text-xs uppercase tracking-[0.25em]"
                            >
                              {isDirectedNudge ? `Nudge ▸ ${directedQueue.length}` : "Send"}
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-600 text-right">
                      {freeText.trim().length} chars · ⌘/Ctrl + Enter to send
                    </div>
                  </div>
                ) : (
                <Tabs defaultValue="choices" className="w-full">
                  <TabsList className="grid grid-cols-2 bg-transparent border border-stone-800 rounded-none mb-8 h-12 p-0">
                    <TabsTrigger data-testid="tab-choices" value="choices" className="rounded-none font-sans-ui text-xs uppercase tracking-[0.2em] data-[state=active]:bg-stone-900 data-[state=active]:text-rose-200">
                      Choose a path
                    </TabsTrigger>
                    <TabsTrigger data-testid="tab-freetext" value="free" className="rounded-none font-sans-ui text-xs uppercase tracking-[0.2em] data-[state=active]:bg-stone-900 data-[state=active]:text-rose-200">
                      Write your own
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="choices" className="space-y-3">
                    {(lastChapter?.choices || []).map((c, i) => (
                      <button
                        key={i}
                        data-testid={`choice-btn-${i}`}
                        onClick={() => continueStory({ choice: c })}
                        className="w-full text-left p-6 border border-stone-800 bg-stone-900/30 hover:bg-stone-900 hover:border-rose-900 transition-all group"
                      >
                        <div className="flex items-start gap-4">
                          <span className="font-serif-heading italic text-rose-800 text-xl leading-none mt-1">{romanLower(i+1)}.</span>
                          <span className="font-serif-reading text-stone-200 text-lg leading-relaxed group-hover:text-rose-100">
                            {c}
                          </span>
                        </div>
                      </button>
                    ))}
                    {/* Author's-choice card: hand the beat to the AI, two flavors */}
                    <div className="w-full p-5 border border-dashed border-stone-700 bg-transparent" data-testid="author-choice-card">
                      <div className="flex items-center gap-3 mb-4">
                        <Feather className="w-3.5 h-3.5 text-rose-800" strokeWidth={1.5} />
                        <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                          Author&rsquo;s choice
                        </span>
                        <div className="flex-1 h-px bg-stone-800/60"></div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button
                          data-testid="choice-btn-ai"
                          onClick={() => continueStory({ ai_decides: true })}
                          className="group p-4 border border-stone-800 bg-stone-900/30 hover:bg-rose-950/15 hover:border-rose-800 text-left transition"
                        >
                          <div className="font-serif-reading italic text-stone-200 text-base group-hover:text-rose-100">
                            Let the story unfold
                          </div>
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500 mt-1.5">
                            natural continuation
                          </div>
                        </button>
                        <button
                          data-testid="choice-btn-surprise"
                          onClick={() => continueStory({ ai_decides: true, twist: true })}
                          className="group p-4 border border-stone-800 bg-stone-900/30 hover:bg-rose-950/15 hover:border-rose-800 text-left transition relative"
                        >
                          <Sparkles className="absolute top-3 right-3 w-3 h-3 text-rose-700/70" strokeWidth={1.5} />
                          <div className="font-serif-reading italic text-stone-200 text-base group-hover:text-rose-100">
                            Surprise me
                          </div>
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500 mt-1.5">
                            take an unexpected turn
                          </div>
                        </button>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="free" className="space-y-4">
                    <Textarea
                      data-testid="free-text-input"
                      value={freeText}
                      onChange={(e) => setFreeText(e.target.value)}
                      placeholder="Describe what should happen next, in a few words or many. Whisper a direction."
                      className="bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading text-lg leading-relaxed focus:ring-rose-900 min-h-[140px]"
                    />
                    {/* Surprise-me modifier: twists the reader's direction in an unexpected way */}
                    <button
                      type="button"
                      data-testid="twist-toggle"
                      onClick={() => setTwistOn(v => !v)}
                      className={`w-full flex items-start gap-3 p-4 border transition text-left ${twistOn
                        ? "border-rose-800 bg-rose-950/25"
                        : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                    >
                      <div className={`mt-0.5 w-4 h-4 border flex items-center justify-center shrink-0 ${twistOn ? "bg-rose-900 border-rose-800" : "border-stone-600 bg-stone-950"}`}>
                        {twistOn && <Check className="w-3 h-3 text-rose-50" strokeWidth={2.5} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 font-sans-ui text-[11px] uppercase tracking-[0.25em] text-stone-300">
                          <Sparkles className="w-3.5 h-3.5 text-rose-700" strokeWidth={1.5} /> Take me somewhere unexpected
                        </div>
                        <div className="font-serif-reading italic text-stone-500 text-xs mt-1 leading-relaxed">
                          {twistOn
                            ? "The AI will resist the obvious reading of your direction."
                            : "Let the AI subvert the obvious path your words suggest."}
                        </div>
                      </div>
                    </button>
                    <Button
                      data-testid="submit-freetext-btn"
                      disabled={!freeText.trim()}
                      onClick={() => { continueStory({ free_text: freeText, twist: twistOn }); setTwistOn(false); }}
                      className="w-full bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 font-sans-ui text-xs uppercase tracking-[0.25em]"
                    >
                      Continue with my direction
                    </Button>
                  </TabsContent>
                </Tabs>
                )}

                <div className="mt-10 pt-8 border-t border-stone-800/40 flex items-center justify-between gap-4 flex-wrap">
                  <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-600">
                    {isChat
                      ? "Each chapter: edit · rewind · regenerate (last only)"
                      : "Each chapter: edit · rewind · regenerate (last only)"}
                  </div>
                  <button
                    data-testid="end-story-btn"
                    onClick={() => { setEndingFlavor("ai"); setEndingCustom(""); setEndDialogOpen(true); }}
                    className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 hover:text-rose-300 border-b border-stone-800 hover:border-rose-800 pb-1 transition"
                  >
                    End the story &rarr;
                  </button>
                </div>
              </>
            )
          ) : null}
        </div>
      </div>

      {/* Player-picker: shown when user opts into texting on a prose-started story */}
      <Dialog open={playerPickerOpen} onOpenChange={(v) => !savingPlayer && setPlayerPickerOpen(v)}>
        <DialogContent className="bg-stone-950 border-stone-800 rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl font-light text-stone-100">
              Pick who you play as
            </DialogTitle>
          </DialogHeader>
          <p className="font-serif-reading italic text-stone-400 text-sm leading-relaxed -mt-2">
            {playerPickerTarget === "scene"
              ? "To switch into scene mode, choose which character you play in the scene. You can switch back to prose or texting any time — the toggle stays."
              : "To switch into texting, choose which character holds the phone in your hand. You can switch back to prose at any time — the toggle stays."}
          </p>
          <div className="space-y-2 mt-3">
            {(characters || []).map((c) => {
              const sel = playerPickerChoice === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`player-pick-${c.id}`}
                  onClick={() => setPlayerPickerChoice(c.id)}
                  className={`w-full p-4 border text-left transition ${sel
                    ? "border-rose-800 bg-rose-950/25"
                    : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                >
                  <div className="font-serif-reading text-stone-100 text-base">{c.name}</div>
                  {c.description && (
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-500 mt-1 line-clamp-1">
                      {c.description}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="ghost"
              data-testid="player-pick-cancel"
              disabled={savingPlayer}
              onClick={() => setPlayerPickerOpen(false)}
              className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-400 hover:text-stone-200"
            >
              Cancel
            </Button>
            <Button
              data-testid="player-pick-confirm"
              disabled={savingPlayer || !playerPickerChoice}
              onClick={enableTextingForStory}
              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none font-sans-ui text-[10px] uppercase tracking-[0.25em]"
            >
              {savingPlayer
                ? "Enabling…"
                : (playerPickerTarget === "scene" ? "Switch to scene mode" : "Switch to texting")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ending flavor picker */}
      <Dialog open={endDialogOpen} onOpenChange={(v) => !generating && setEndDialogOpen(v)}>
        <DialogContent className="bg-stone-950 border-stone-800 rounded-none max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-3xl font-light text-stone-100">
              Bring the story to a close
            </DialogTitle>
          </DialogHeader>
          <p className="font-serif-reading italic text-stone-400 text-sm leading-relaxed -mt-2">
            A final chapter will be written. Choose the shape you want it to take &mdash;
            you can still edit the prose afterwards.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {ENDING_FLAVORS.map(({ key, label, hint, Icon }) => {
              const sel = endingFlavor === key;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`ending-flavor-${key}`}
                  onClick={() => setEndingFlavor(key)}
                  className={`p-4 border text-left transition group ${sel
                    ? "border-rose-800 bg-rose-950/25"
                    : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                >
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <Icon className={`w-3.5 h-3.5 ${sel ? "text-rose-400" : "text-stone-500 group-hover:text-stone-300"}`} strokeWidth={1.5} />
                    <span className={`font-serif-reading text-base ${sel ? "text-rose-100 italic" : "text-stone-200"}`}>{label}</span>
                  </div>
                  <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-500">
                    {hint}
                  </div>
                </button>
              );
            })}
            <button
              type="button"
              data-testid="ending-flavor-custom"
              onClick={() => setEndingFlavor("custom")}
              className={`p-4 border text-left transition group sm:col-span-2 ${endingFlavor === "custom"
                ? "border-rose-800 bg-rose-950/25"
                : "border-dashed border-stone-700 bg-transparent hover:border-rose-800"}`}
            >
              <div className="flex items-center gap-2.5 mb-1.5">
                <PenLine className={`w-3.5 h-3.5 ${endingFlavor === "custom" ? "text-rose-400" : "text-stone-500 group-hover:text-stone-300"}`} strokeWidth={1.5} />
                <span className={`font-serif-reading text-base ${endingFlavor === "custom" ? "text-rose-100 italic" : "text-stone-200"}`}>
                  Write your own
                </span>
              </div>
              <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-500">
                Describe exactly how the story should end
              </div>
            </button>
          </div>
          {endingFlavor === "custom" && (
            <Textarea
              data-testid="ending-custom-input"
              autoFocus
              value={endingCustom}
              onChange={(e) => setEndingCustom(e.target.value)}
              placeholder="e.g. They part at dawn at the Hauptbahnhof. She boards the train. He doesn't look back until it's gone."
              className="bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading text-base leading-relaxed focus:ring-rose-900 min-h-[110px]"
            />
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              data-testid="end-story-cancel"
              disabled={generating}
              onClick={() => setEndDialogOpen(false)}
              className="rounded-none h-10 px-4 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[11px] uppercase tracking-[0.25em]"
            >
              Not yet
            </Button>
            <Button
              data-testid="end-story-confirm"
              disabled={generating || (endingFlavor === "custom" && !endingCustom.trim())}
              onClick={() => {
                setEndDialogOpen(false);
                endStory({ flavor: endingFlavor, custom: endingFlavor === "custom" ? endingCustom.trim() : "" });
              }}
              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-10 px-5 font-sans-ui text-[11px] uppercase tracking-[0.25em]"
            >
              Write the ending
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline rewrite popover — appears near text selections inside chapters */}
      {selectionAnchor && !rewriteResult && (() => {
        const viewportW = typeof window !== "undefined" ? window.innerWidth : 1024;
        const viewportH = typeof window !== "undefined" ? window.innerHeight : 768;
        const popW = 320;
        const popH = 260; // estimate
        const stickyBottom = 110; // navbar + sticky bar
        const spaceAbove = selectionAnchor.top - stickyBottom;
        const placeBelow = spaceAbove < popH + 16;
        const topPx = placeBelow
          ? Math.min(viewportH - popH - 16, selectionAnchor.bottom + 12)
          : Math.max(stickyBottom + 8, selectionAnchor.top - popH - 12);
        const leftPx = Math.min(Math.max(12, selectionAnchor.x - popW / 2), viewportW - popW - 12);
        return (
        <div
          ref={popoverRef}
          data-rewrite-popover
          data-testid="rewrite-popover"
          onMouseDown={(e) => e.preventDefault()}
          style={{ position: "fixed", left: leftPx, top: topPx, zIndex: 60 }}
          className="w-[320px] bg-stone-950 border border-stone-700 shadow-2xl shadow-black/70 p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Wand2 className="w-3.5 h-3.5 text-rose-700" strokeWidth={1.5} />
            <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-400">Rewrite this passage</div>
          </div>
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            {REWRITE_PRESETS.map(p => (
              <button
                key={p.key}
                data-testid={`rewrite-preset-${p.key}`}
                disabled={!!rewriteBusy}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => requestRewrite(p.key, p.instruction, p.label)}
                className="border border-stone-800 bg-stone-900/60 hover:bg-rose-950/30 hover:border-rose-800 text-stone-200 font-sans-ui text-[11px] uppercase tracking-[0.2em] px-2 py-2 transition disabled:opacity-40 disabled:cursor-wait text-left"
              >
                {rewriteBusy === p.key ? (
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> {p.label}…</span>
                ) : p.label}
              </button>
            ))}
          </div>
          {!showCustomField ? (
            <button
              data-testid="rewrite-custom-toggle"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowCustomField(true)}
              className="w-full border border-dashed border-stone-700 bg-transparent hover:border-rose-800 text-stone-400 hover:text-rose-200 font-sans-ui text-[11px] uppercase tracking-[0.2em] px-2 py-2 transition"
            >
              Write my own instruction…
            </button>
          ) : (
            <div className="space-y-2">
              <Input
                autoFocus
                data-testid="rewrite-custom-input"
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && customInstruction.trim()) {
                    e.preventDefault();
                    requestRewrite("custom", customInstruction.trim(), "Custom");
                  }
                }}
                placeholder="e.g. more cinematic, slower heartbeat…"
                className="bg-stone-900 border-stone-800 rounded-none h-9 text-sm text-stone-100 font-serif-reading focus:ring-rose-900"
              />
              <Button
                data-testid="rewrite-custom-submit"
                disabled={!customInstruction.trim() || !!rewriteBusy}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => requestRewrite("custom", customInstruction.trim(), "Custom")}
                className="w-full bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-9 font-sans-ui text-[11px] uppercase tracking-[0.25em]"
              >
                {rewriteBusy === "custom" ? (
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> Rewriting</span>
                ) : "Rewrite"}
              </Button>
            </div>
          )}
        </div>
        );
      })()}

      {/* Before / after diff modal */}
      <Dialog open={!!rewriteResult} onOpenChange={(v) => !v && !savingRewrite && setRewriteResult(null)}>
        <DialogContent className="bg-stone-950 border-stone-800 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-3xl font-light text-stone-100">
              A rewrite for your blessing
            </DialogTitle>
          </DialogHeader>
          {rewriteResult && (
            <div className="space-y-5">
              <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800">
                Instruction · {rewriteResult.instructionLabel}
              </div>
              <div>
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Original</div>
                <div data-testid="rewrite-diff-original" className="border border-stone-800 bg-stone-900/40 p-4 font-serif-reading text-stone-400 italic text-sm leading-relaxed whitespace-pre-wrap">
                  {rewriteResult.original}
                </div>
              </div>
              <div>
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-rose-300 mb-2">Rewritten</div>
                <div data-testid="rewrite-diff-rewritten" className="border border-rose-900/60 bg-rose-950/15 p-4 font-serif-reading text-stone-100 text-sm leading-relaxed whitespace-pre-wrap">
                  {rewriteResult.rewritten}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              data-testid="rewrite-dismiss-btn"
              disabled={savingRewrite}
              onClick={() => setRewriteResult(null)}
              className="rounded-none h-10 px-4 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[11px] uppercase tracking-[0.25em]"
            >
              Dismiss
            </Button>
            <Button
              data-testid="rewrite-apply-btn"
              disabled={savingRewrite}
              onClick={applyRewrite}
              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-10 px-5 font-sans-ui text-[11px] uppercase tracking-[0.25em]"
            >
              {savingRewrite ? "Applying…" : "Apply rewrite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function toRoman(num) {
  const map = [[1000,"M"],[900,"CM"],[500,"D"],[400,"CD"],[100,"C"],[90,"XC"],[50,"L"],[40,"XL"],[10,"X"],[9,"IX"],[5,"V"],[4,"IV"],[1,"I"]];
  let r = "";
  for (const [v, s] of map) { while (num >= v) { r += s; num -= v; } }
  return r || "I";
}
function romanLower(n) { return toRoman(n).toLowerCase(); }

// Derive the reader directive that led to the NEXT chapter (stored on `prior`).
function getEpigraph(prior) {
  if (!prior) return null;
  const cc = (prior.chosen_choice || "").trim();
  const ui = (prior.user_input || "").trim();
  const ai = !!prior.ai_decided;
  const tw = !!prior.twist;
  if (cc.startsWith("[end:")) {
    const flavor = cc.endsWith("]") ? cc.slice(5, -1) : cc.slice(5);
    const labels = {
      happy: "Ending · Happy",
      bittersweet: "Ending · Bittersweet",
      tragic: "Ending · Tragic",
      open: "Ending · Open",
      cliffhanger: "Ending · Cliffhanger",
      ai: "Ending · Author\u2019s choice",
      custom: "Ending · Custom",
    };
    return { label: labels[flavor] || `Ending · ${flavor}`, text: ui || null };
  }
  if (ai) return { label: tw ? "Author\u2019s choice \u2014 surprise" : "Author\u2019s choice", text: null };
  if (ui) return { label: tw ? "A direction written (with a twist)" : "A direction written", text: ui };
  if (cc) return { label: "The path chosen", text: cc };
  return null;
}

export default StoryReader;
